import type { TFunction } from 'i18next';
import * as yup from 'yup';

import {
    MeasureKey,
    PrognosisDetailsDto,
    PrognosisMetricKey,
    RecommendedHeatingSystemKey,
    SteckbriefKind,
    VariantPrefillDto,
} from './ensan-steckbrief.dto';
import { dropAssumptionDefaults } from './steckbrief-assumption-defaults';

/**
 * Generated-by-hand mirror of the EnSan Steckbrief field mapping
 * (`dev-context/features/ensan-steckbrief/field-mapping.md`) — KEEP IN SYNC.
 *
 * One entry per `form field` row of the mapping, in mapping order: key (== form
 * key == template placeholder), German label (via i18next), type, print section
 * (template block), criticality and kind-conditional flags. The backend's
 * `VariantFieldMapping` is the same table's code mirror on the other side; the
 * T18 contract test will assert form keys == formSchema keys, so any change to
 * the mapping doc must land here too.
 *
 * Two mapping form-field rows are deliberately NOT part of this value model
 * (matching the backend `VariantFieldMapping`, which rejects them in `values`):
 * - `kind` (#49) — own column of the Variant (CreateVariantRequest.kind),
 * - `selectedMeasures` (#38) — travels in the dedicated measures payload
 *   (CreateVariantRequest.measures), not in `values`.
 *
 * `steckbriefCode` (#1) is also deliberately absent although the backend
 * mapping and the `/variants/prefill` response include it: it is a computed
 * placeholder (the Steckbrief's own "SB-NNN" code, minted server-side), not a
 * form field — the form never renders it and never submits it in `values`; the
 * prefill entry is simply dropped when the form builds its initial values from
 * this field model (see [PREFILL_DROPPED_KEYS]).
 *
 * Every OTHER prefill key outside this model is a CARRIED LEGACY VALUE
 * (GEN-750): a key the backend `VariantFieldMapping` still owns — so legacy
 * stamps keep validating and their values keep carrying forward — but that the
 * form no longer renders. Today that is `windowReplacementYear` (#30b), retired
 * as a form field when GEN-750 folded the window year into the
 * `windowReplacement` tri-state (the GEN-748 inline-year mechanism) — and,
 * depending on the backend's GEN-753 retirement shape, possibly
 * `electricalInstallationRenovation` (#34) and `fullRenovation` (#35), removed
 * from the form by GEN-753 (see the Sanierungshistorie section) — and the
 * funding gates `budgetConsolidation`, `nonEconomicUse`, `klimaKommune`
 * (#46–#48), removed from the form by GEN-779 with the engine funding output
 * they fed (see the Förderung section). Carried legacy values must pass through the form VERBATIM: into `/calculate` (so the
 * preview sees the same facts as the eventual stamp) and into the save payload
 * (so the value is never lost) — see [carriedLegacyKeys].
 */

export type VariantFieldType =
    | 'TEXT'
    | 'MULTILINE_TEXT'
    | 'NUMBER'
    | 'YEAR'
    | 'SELECT'
    | 'MULTI_SELECT'
    | 'TRI_STATE'
    | 'CONSUMPTION_ENTRIES'
    | 'ATTACHMENT'
    // A calendar month, value "YYYY-MM" (GEN-786: the printed Stand).
    | 'MONTH';

/**
 * One year-tagged consumption entry (GEN-744) — the value shape of a
 * CONSUMPTION_ENTRIES field is `ConsumptionEntry[] | null` (byte-identical with
 * the backend's list). The form collects ONE entry per carrier for now; the
 * list shape is deliberate so multiple years can follow without a migration.
 * Nulls exist only transiently in form state (a half-typed pair) — the schema
 * blocks saving an incomplete pair, so persisted entries always carry both.
 * A string kWh is typed text that is no number (the kWh input keeps it, like
 * the tariff inputs): the schema blocks the save, `/calculate` reads it as
 * unknown.
 */
export interface ConsumptionEntry {
    year: number | null;
    consumptionKwh: number | string | null;
}

/** Criticality for the FINAL kind (mapping column "Criticality (final kind)"). */
export type VariantFieldCriticality = 'MANDATORY' | 'IMPORTANT' | 'OPTIONAL';

/** Print sections (template blocks) the form mirrors, in template order. */
export type VariantFormSection =
    // The print's header line (GEN-786): Dok-Nr. (computed) and the Stand month.
    | 'HEADER'
    | 'ECKDATEN'
    | 'BUILDING_PHOTO'
    | 'ENERGIE_WAERME'
    | 'SANIERUNGSHISTORIE'
    | 'HINWEISE_KOMMUNE'
    | 'FOERDERUNG'
    | 'BOTTOM_SECTION'
    // Advisor-adjusted Prognosewerte (GEN-782) — rendered by the form's own
    // Prognose panel, NOT by the generic section grid (absent from
    // VARIANT_FORM_SECTIONS on purpose).
    | 'PROGNOSE'
    // Inputs of the investment cost estimate shown next to the KPI investment
    // totals (the cost factor) — rendered by the form's KPI box, NOT by the
    // generic section grid (absent from VARIANT_FORM_SECTIONS on purpose).
    | 'INVESTMENT'
    // The lookalike reference context (GEN-700 D4) — copied from the search response by the
    // Gebäudevergleich, never rendered as inputs (absent from VARIANT_FORM_SECTIONS on purpose).
    | 'LOOKALIKE';

/**
 * How a tri-state renders: renovation history rows use saniert/teilsaniert/
 * unsaniert/unbekannt, boolean facts use ja/nein/unbekannt. Both map onto
 * true/false/null values (the `unbekannt` dot IS the null value — mapping
 * "Reading the table"); a RENOVATION row additionally carries the renovation
 * year inline (GEN-748) or the teilsaniert value [RENOVATION_PARTIAL_VALUE]
 * (GEN-788). A BOOLEAN row (GEN-801, "Is NGF known?"; the QA feedback round's
 * "Is the heating construction year known?") offers ja/nein only —
 * null is no answer there, never "unknown".
 */
export type TriStateFlavor = 'RENOVATION' | 'YES_NO' | 'BOOLEAN';

/**
 * The teilsaniert value of a RENOVATION tri-state (GEN-788) — mirror of the
 * backend `RenovationStatus.PARTIAL` name, matched EXACTLY by the facts mapper
 * and the print template (one encoding everywhere, no German literal). It has
 * no year. The prognosis model scores it 1 (between saniert 0 and offen 2); the
 * measure rules treat it like offen: the measure is proposed and the input
 * counts as collected data, so no "assumed" entry fires. YES_NO tri-states
 * never accept it.
 */
export const RENOVATION_PARTIAL_VALUE = 'PARTIAL';

export interface VariantFieldOption {
    /** Value sent to the backend (English enum-style code). */
    value: string;
    /** i18next key; the de catalog carries the German print label. */
    labelKey: string;
}

/**
 * Visibility condition of a conditional field (mapping #33, GEN-739): the field
 * renders only while the gating field currently holds `value`. One mechanism
 * for both gate shapes:
 * - tri-state gates use `value: true` (the historical `conditionalOnTrue`),
 * - SELECT gates use the option code (strict equality),
 * - MULTI_SELECT gates match while the code is AMONG the selected values.
 */
export interface VariantFieldCondition {
    /** Key of the gating field (must be a form key of this model). */
    field: string;
    /** Gate value: `true` for tri-states, an option code for (multi-)selects. */
    value: boolean | string;
}

/** Whether `condition` is currently satisfied by the form values. */
export function conditionSatisfied(condition: VariantFieldCondition, values: Record<string, unknown>): boolean {
    const gateValue = values[condition.field];
    if (Array.isArray(gateValue)) return gateValue.includes(condition.value);
    return gateValue === condition.value;
}

/**
 * Whether a field renders for these values: its condition holds AND its gate
 * renders itself (GEN-801 chains gates: "Other renewable source" hangs off the
 * sources list, which hangs off "Existing renewable energy?" — a hidden list
 * must hide its companion too, whatever the list still holds in form state).
 */
export function fieldVisibleIn(field: SteckbriefVariantField, values: Record<string, unknown>): boolean {
    if (!field.conditionalOn) return true;
    if (!conditionSatisfied(field.conditionalOn, values)) return false;
    const gate = FIELDS_BY_KEY.get(field.conditionalOn.field);
    return gate === undefined || fieldVisibleIn(gate, values);
}

export interface SteckbriefVariantField {
    /** Form key == formSchema key == template placeholder (field-mapping rule). */
    key: string;
    /** i18next key; the de catalog carries the mapping's German label. */
    labelKey: string;
    type: VariantFieldType;
    section: VariantFormSection;
    criticality: VariantFieldCriticality;
    triStateFlavor?: TriStateFlavor;
    /**
     * Static select options. `buildingFunction` has none here — its options are
     * the fixed [STECKBRIEF_BUILDING_FUNCTION_CODES], labelled by the ADV
     * GebäudeFunktion register (`src/common/buildingFunctions.json`).
     */
    options?: VariantFieldOption[];
    /**
     * Options retired from `options` (GEN-701, GEN-788) whose codes may still
     * appear in saved stamps or carried-forward values: renderers localize them
     * like live options, but they are never offered for new selection.
     */
    retiredOptions?: VariantFieldOption[];
    /**
     * Visible only while the gating field holds the condition value (mapping
     * #33 tri-state gates, GEN-739 Other-option gates); nulled in the save
     * payload while hidden (the typed value stays in form state).
     */
    conditionalOn?: VariantFieldCondition;
    /** Help text (engine inputs, print-only free texts, manual funding values). */
    helpTextKey?: string;
    /**
     * Hard character limit (GEN-745), enforced by the FORM: the input accepts
     * any length (no native clipping — QA feedback: a pasted text must not be
     * cut silently) and shows a live "n/max" counter; over the limit it shows
     * [CHARACTER_LIMIT_EXCEEDED_MSG] and the values schema blocks the save.
     * Frontend-only enforcement on purpose: the backend accepts any string for
     * these user-only fields and this form is their only writer — the same
     * trust model as the GEN-739 Other companions and `waterLeakageLocation`.
     */
    maxLength?: number;
    /**
     * GEN-748: this RENOVATION tri-state can carry the renovation year INLINE
     * in its value — the backend contract, not a new one: saniert + known year
     * stores the year (a NUMBER), saniert without year stays `true`, offen
     * `false`, unbekannt `null`. The backend already reads a numeric tri-state
     * as the renovation year (`SteckbriefFactsMapper.renovationYear`), the
     * formSchema types these keys as COMPUTED (value-shape tolerant) and the
     * print template renders the numeric value with the done-dot ("● 2018") —
     * no backend schema or template change. Since GEN-750 every RENOVATION
     * tri-state is flagged, including `windowReplacement` (see its entry for
     * the retired standalone `windowReplacementYear` field's history). Since
     * GEN-788 the value may also be [RENOVATION_PARTIAL_VALUE] (teilsaniert,
     * never with a year).
     */
    renovationYearInput?: boolean;
    /**
     * Layout hint of the two-column section grid (GEN-786): the field always
     * starts a new row in the LEFT column, so paired fields (an item and its
     * note, a gate and its conditional companion) line up whatever the
     * conditional fields before them render. Display only — no effect on
     * values or payloads.
     */
    startsRow?: boolean;
    /**
     * Derived numbers only (GEN-786): the value is shown and seeded rounded to
     * at most this many decimals — see [roundDerivedPrefill].
     */
    maxDecimals?: number;
    /**
     * No input (GEN-798 follow-up): the building-surface figures are not
     * editable. The value still flows exactly like a field's — prefilled from
     * the catalog (or derived by `/calculate` for the ENGINE_ECHO keys),
     * submitted to every `/calculate` run, stamped on save — but it is SHOWN
     * only in the "Assumptions & derived values" section, for explainability.
     * No validation either: nothing a user could fix is ever rendered.
     */
    displayOnly?: boolean;
    /**
     * NUMBER fields whose value is a small decimal (GEN-788, the tariffs in
     * €/kWh): rendered as a text input that accepts the German decimal comma
     * as well as the point ([parseDecimalInput]) instead of the browser's
     * locale-dependent number input. Text that is no number stays in the form
     * state verbatim, so the values schema blocks the save with its message.
     */
    decimalInput?: boolean;
    /**
     * No default selection (GEN-801): a choice field whose "unknown" is an
     * ANSWER the advisor gives, never a preselection. The value model is
     * unchanged — unbekannt is still null — so the form tracks which fields were
     * answered (see [ANSWERED_KEYS_CONTEXT]): a null nobody chose renders with no
     * option selected and, when the field is required, blocks calculate and save.
     */
    explicitAnswer?: boolean;
    /**
     * SELECT fields rendered as a row of buttons with a trailing "unknown"
     * (GEN-801: Verglasung, Beleuchtung — "like the renovation items") instead
     * of a dropdown. Values and payload are those of the dropdown.
     */
    buttons?: boolean;
}

/**
 * Yup context key of the answered-field set (GEN-801): the keys of the
 * [SteckbriefVariantField.explicitAnswer] fields the advisor answered —
 * "unknown" included. Without it every null of such a field counts as
 * unanswered.
 */
export const ANSWERED_KEYS_CONTEXT = 'answeredKeys';

/** Inline errors of a required field (GEN-801) — i18next keys, the de catalog carries the German wording. */
export const REQUIRED_FIELD_MSG = 'This field is required.';
export const ANSWER_REQUIRED_MSG = 'Please select an option.';
export const RENEWABLE_SOURCES_REQUIRED_MSG = 'Please select at least one source.';
export const CONSUMPTION_REQUIRED_MSG = 'Please enter the consumption and its year.';

/**
 * Character budget of a Sanierungshistorie note (GEN-745) — the print template
 * shows ONE line under the item (`<div class="hd">`, ~103px at 7.7px). Layout v4
 * (GEN-775) fills the page exactly at design content, so the note must stay on
 * its line: 25 characters (was 60 — three lines — before v4).
 */
export const RENOVATION_NOTE_MAX_LENGTH = 25;

/**
 * Print limits of the other free texts (GEN-775, layout v4 — dev-context
 * field-mapping.md "Print limits"): each keeps its text inside the line budget
 * the design gives the slot, so the one-page print never overflows.
 */
export const PRINT_LIMIT_OWNER = 28;
/**
 * The printed Gebäudeart: up to two Eckdaten value lines, like the owner
 * (GEN-801 sheet, tested by product; was 21 — one line — since GEN-794).
 */
export const PRINT_LIMIT_BUILDING_FUNCTION = 28;

/**
 * The Gebäudetyp options (#3): the building functions the PV Analysis tool
 * supports (pv-calculator `BuildingFunction` + its additional mapping) plus
 * the admi-custom "Schule mit Sporthalle" (`99500_0002`), which the backend
 * engines evaluate as Allgemein bildende Schule. Labels come from the ADV
 * register; the form sorts them by name.
 */
export const STECKBRIEF_BUILDING_FUNCTION_CODES: string[] = [
    '99500_0001', // Bauhof
    '31001_3037', // Bibliothek, Bücherei
    '31001_3072', // Feuerwehr
    '31001_3222', // Gebäude im Freibad
    '31001_3080', // Friedhofsgebäude
    '31001_3221', // Hallenbad
    '31001_3061', // Jugendfreizeitheim
    '31001_3062', // Freizeit-, Vereinsheim, Dorfgemeinschafts-, Bürgerhaus
    '31001_3065', // Kindergarten
    '31001_2611', // Gebäude der Kläranlage
    '31001_3034', // Museum
    '31001_2591', // Pumpwerk (nicht für Wasserversorgung)
    '31001_3021', // Allgemein bildende Schule
    '99500_0002', // Schule mit Sporthalle
    '31001_3211', // Sport-, Turnhalle
    '31001_3212', // Gebäude zum Sportplatz
    '31001_3010', // Verwaltungsgebäude
    '31001_2511', // Wasserwerk
    '31001_3066', // Asylbewerberheim
];
export const PRINT_LIMIT_ADDRESS = 60;
export const PRINT_LIMIT_ENERGY_OTHER_TEXT = 16;
/**
 * The two free-text cards print two lines each (GEN-801 sheet, tested by
 * product; one line / 130 since GEN-786) — the print layout was refitted.
 */
export const PRINT_LIMIT_MUNICIPALITY_NOTES = 260;
export const PRINT_LIMIT_EXTRA_INFORMATION = 260;
/**
 * The funding Hinweis (GEN-779): three lines (GEN-801 sheet, tested by product;
 * two lines / 150 since GEN-786).
 */
export const PRINT_LIMIT_FUNDING_COMMENT = 220;
/** The Steckbrief name is the printed title (one line). */
export const PRINT_LIMIT_STECKBRIEF_NAME = 60;

/**
 * Decimal budget of every DERIVED number of the form (GEN-786): the geometry
 * metrics (data-products + the backend's multi-building sum), the engine echo
 * (window surface area). The backend rounds them at the source;
 * the form applies the same bound to values from OLDER stamps, which may still
 * carry floating-point noise from before the backend fix.
 */
export const DERIVED_NUMBER_MAX_DECIMALS = 2;

/** Help text of the Stand month (GEN-786). */
export const AS_OF_DATE_HELP_TEXT_KEY =
    'Printed as "Stand" in the Steckbrief header — month and year only. Defaults to the current month.';

/** Inline error of a malformed Stand value (GEN-786). */
export const AS_OF_DATE_FORMAT_MSG = 'Please pick a month.';

/** The current month as a Stand value ("YYYY-MM") — the seed of a new Variant (GEN-786). */
export function currentAsOfMonth(now: Date = new Date()): string {
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** Shared help text of the five Sanierungshistorie notes (GEN-745). */
export const RENOVATION_NOTE_HELP_TEXT_KEY = 'Printed in the Steckbrief under this item.';

/**
 * Caption of the building-surfaces block in "Assumptions & derived values"
 * (GEN-773, reworded for GEN-798): the surface figures come from the building
 * geometry, are not editable, and feed the item-by-item investment estimate.
 */
export const BUILDING_GEOMETRY_METRIC_HELP_TEXT_KEY =
    'Prefilled from the building geometry — input of the investment cost estimate.';

/**
 * Inline error of the window-area share plausibility bounds (GEN-774),
 * 0 < share ≤ 100 — exported as an i18next key like the GEN-744 consumption
 * messages; the de catalog carries the German wording.
 */
export const WINDOW_AREA_SHARE_RANGE_MSG = 'The window area share must be greater than 0 and at most 100.';

/** Help text of the window-area share field (GEN-774; base = the real surface area since GEN-798). */
export const WINDOW_AREA_SHARE_HELP_TEXT_KEY =
    'Share of the real surface area counted as windows — the window surface area derives as real surface area × share.';

/**
 * Help texts of the two tariffs of the cost-saving prognosis (GEN-788, sheet v6
 * Calculator B11 / B17): empty = the EVEBI default, which the field shows as
 * its placeholder (see [ENERGY_PRICE_PROGNOSIS_KEYS]).
 */
export const HEAT_PRICE_HELP_TEXT_KEY =
    "The municipality's actual heat tariff — leave empty to use the EVEBI default of the energy carrier. Input of the cost saving.";
export const ELECTRICITY_PRICE_HELP_TEXT_KEY =
    "The municipality's contracted electricity price, the most sensitive input of the cost saving — leave empty to use the EVEBI default.";

/**
 * Inline error of a text over its character limit (QA feedback round) — an
 * i18next key with the live length and the limit, e.g. "… - 103/60".
 */
export const CHARACTER_LIMIT_EXCEEDED_MSG = 'Character limit exceeded - {{length}}/{{max}}';

/** Whether a text value is longer than its field's character limit. */
export function exceedsMaxLength(field: Pick<SteckbriefVariantField, 'maxLength'>, value: unknown): boolean {
    return field.maxLength !== undefined && typeof value === 'string' && value.length > field.maxLength;
}

/**
 * Investment cost factor (QA feedback round): scales every investment cost
 * estimate (all measures, incl. PV) by factor / 100 — 100 = the recommended
 * costs. The form seeds the default like the window share; the backend treats
 * a missing / non-finite / non-positive value as 100.
 */
export const DEFAULT_INVESTMENT_COST_FACTOR_PERCENT = 100;
export const INVESTMENT_COST_FACTOR_MIN_PERCENT = 1;
export const INVESTMENT_COST_FACTOR_MAX_PERCENT = 500;
export const INVESTMENT_COST_FACTOR_RANGE_MSG = 'The investment cost factor must be between 1 and 500 %.';
export const INVESTMENT_COST_FACTOR_HELP_TEXT_KEY =
    '100 % = the recommended investment costs. Increase to price higher, decrease to price lower.';

/**
 * The CityGML-derived building figures (GEN-773 / GEN-798): prefilled from
 * the building geometry (`building_api` leaves), inputs of the item-by-item
 * investment estimate. A missing one raises the form's "missing building
 * geometry" warning.
 */
export const CITYGML_FIELD_KEYS = ['footprintArea', 'rawSurfaceArea', 'realSurfaceArea', 'adjacentBuildingCount'];

/** The CityGML-derived figures without a value, in model order. */
export function missingCityGmlFieldKeys(values: Record<string, unknown>): string[] {
    return CITYGML_FIELD_KEYS.filter((key) => values[key] === null || values[key] === undefined);
}

/**
 * Plausibility bound of an entered tariff (GEN-788): 0 < price ≤ 1 €/kWh. The
 * form blocks anything else; the backend stays lenient (it reads any finite
 * positive value and falls back to the EVEBI default otherwise).
 */
export const ENERGY_PRICE_MAX_EUR_PER_KWH = 1;
export const ENERGY_PRICE_RANGE_MSG = 'The price must be greater than 0 and at most 1 €/kWh.';
/** Inline error of typed text that is no number — the same key as the Prognose panel's editor. */
export const ENERGY_PRICE_NOT_A_NUMBER_MSG = 'Please enter a number.';

/**
 * The two tariff fields (GEN-788) → the `PrognosisDetailsDto` field echoing
 * their EFFECTIVE value. Unlike [ENGINE_ECHO_FIELD_KEYS] the echo is never
 * written into the field or the stamp: an empty tariff stays empty (stamped
 * only when the advisor entered one) and the form shows the EVEBI default as
 * the placeholder.
 */
export const ENERGY_PRICE_PROGNOSIS_KEYS = {
    heatPrice: 'heatPriceEurPerKwh',
    electricityPrice: 'electricityPriceEurPerKwh',
} as const satisfies Record<string, keyof PrognosisDetailsDto>;

export type EnergyPriceFieldKey = keyof typeof ENERGY_PRICE_PROGNOSIS_KEYS;

export function isEnergyPriceField(key: string): key is EnergyPriceFieldKey {
    return Object.hasOwn(ENERGY_PRICE_PROGNOSIS_KEYS, key);
}

/**
 * Whether a submitted tariff value counts as ENTERED — mirror of the backend's
 * lenient gate (a finite positive number; anything else means the EVEBI
 * default applies).
 */
export function isEnteredEnergyPrice(value: unknown): boolean {
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Parses a typed decimal (GEN-788, the tariffs): a comma or a point as the
 * decimal separator ("0,41" / "0.41"), no thousands separators. Null for blank
 * text, NaN for anything that is no number.
 */
export function parseDecimalInput(text: string): number | null {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    const match = /^(-?\d+)(?:[.,](\d+))?$/.exec(trimmed);
    if (!match) return Number.NaN;
    const [, integer, fraction] = match;
    return Number(fraction === undefined ? integer : `${integer}.${fraction}`);
}

/** A number as the decimal input shows it: the de comma, the en point, no grouping. */
export function formatDecimalInput(value: number, language: string | undefined): string {
    const text = String(value);
    return language !== undefined && language.startsWith('de') ? text.replace('.', ',') : text;
}

/**
 * Parses a typed quantity that may carry German thousands separators — the
 * kWh consumption values. A point that groups EXACTLY three digits is a
 * thousands separator, as an advisor writes it: "7.842" is 7 842 and
 * "48.463,3" is 48 463,3 (a leading 0 never groups: "0.123" stays a decimal).
 * The browser's number input read "7.842" as 7,842 kWh (SB-010595987008-01,
 * 2026-10-14). Anything else follows parseDecimalInput: a
 * comma or a point as the decimal separator ("48463,3" / "48463.3"). Null for
 * blank text, NaN for anything that is no number.
 */
export function parseGroupedDecimalInput(text: string): number | null {
    const trimmed = text.trim();
    const grouped = /^([1-9]\d{0,2}(?:\.\d{3})+)(?:,(\d+))?$/.exec(trimmed);
    if (!grouped) return parseDecimalInput(trimmed);
    const [, integer, fraction] = grouped;
    const digits = integer.replaceAll('.', '');
    return Number(fraction === undefined ? digits : `${digits}.${fraction}`);
}

/**
 * A number as the grouped decimal input shows it: de with thousands points and
 * the decimal comma ("48.463,3" — what parseGroupedDecimalInput reads back),
 * en ungrouped with the decimal point.
 */
export function formatGroupedDecimalInput(value: number, language: string | undefined): string {
    if (language === undefined || !language.startsWith('de')) return String(value);
    return value.toLocaleString('de-DE', { useGrouping: true, maximumFractionDigits: 20 });
}

/**
 * The engine-DERIVED values echoed by `/calculate` (GEN-774 / GEN-798): each
 * form key is also the `SteckbriefCalculationResultDto` field carrying its
 * effective value. Display-only since the GEN-798 follow-up: the form keeps
 * them null (every run derives them), the "Assumptions & derived values"
 * section shows the echo, and a save stamps the echo — so generation replays
 * exactly what was shown.
 */
export const ENGINE_ECHO_FIELD_KEYS = ['windowSurfaceArea', 'facadeSurfaceArea'] as const;

export type EngineEchoFieldKey = (typeof ENGINE_ECHO_FIELD_KEYS)[number];

export function isEngineEchoField(key: string): key is EngineEchoFieldKey {
    return (ENGINE_ECHO_FIELD_KEYS as readonly string[]).includes(key);
}

/**
 * Help text of the two manual funding percentages (GEN-779): how the print
 * reads the entered range (the template collapses equal bounds and prints a
 * single bound as "ab"/"bis").
 */
export const FUNDING_PERCENTAGE_HELP_TEXT_KEY =
    'Printed as the projected funding rate (0–100 %): both values give a range, a single value prints as "ab"/"bis".';

/** Help text of the manual funding comment (GEN-779; wording from the GEN-801 sheet). */
export const FUNDING_COMMENT_HELP_TEXT_KEY =
    'Further details about funding, such as information about the funding programs, their requirements, timelines, and factors affecting the funding percentage. Printed under "Hinweis:" in the funding section. Specify if the predicted funding percentage is based on a full or partial renovation, e.g. "Die prognostizierte Förderquote basiert auf einer Komplettsanierung."';

/** Help text of the municipality notes (#36, wording from the GEN-801 sheet). */
export const MUNICIPALITY_NOTES_HELP_TEXT_KEY =
    'Further details about what the customer told us about this building, e.g. details about known damage, planned works, prioritisation of this building in their portfolio. If you have no additional details, please write "Keine zusätzlichen Informationen". Printed in the section "Zusätzliche Hinweise der Kommune".';

/**
 * Help text of the admi information (#50, full wording from the GEN-801 sheet,
 * incl. the standard sentences and their conditions — the form also offers
 * them as one-click suggestions). Multi-line: the helper text keeps the breaks.
 */
export const EXTRA_INFORMATION_HELP_TEXT_KEY =
    'Further explanation about any information, recommendations or predictions in the Steckbrief. If you have no additional details, please write "Keine zusätzlichen Informationen". Printed in the section "Zusätzliche Informationen von admi".\n\n' +
    'Sentences to include:\n' +
    'If the building has Denkmalschutz: "Das Gebäude steht unter Denkmalschutz; einzelne Maßnahmen sind genehmigungspflichtig oder nur in angepasster Ausführung umsetzbar."\n' +
    'If the current heating system is <20 years old and we recommend a heat pump in the future: "Wir empfehlen, die Heizungsanlage nach 20 Jahren zu erneuern, für dieses Gebäude also im Jahr [year]."';

/** Help text of the owner (#8, GEN-801). */
export const OWNER_HELP_TEXT_KEY = 'Write the name of the customer here, e.g. "Gemeinde Musterberg".';

/**
 * The sentences the GEN-801 sheet asks the admi information (#50) to include,
 * each under its condition — offered as one-click suggestions under the field.
 * German print text: the field prints verbatim, so these are NOT translated.
 */
export const EXTRA_INFORMATION_MONUMENT_SENTENCE =
    'Das Gebäude steht unter Denkmalschutz; einzelne Maßnahmen sind genehmigungspflichtig oder nur in angepasster Ausführung umsetzbar.';

/**
 * The heating renewal sentence — for a heating system younger than
 * [HEATING_RENEWAL_AGE_YEARS] while the heat pump is among the selected
 * measures: renew it once it is that old, i.e. in construction year + 20.
 */
export const HEATING_RENEWAL_AGE_YEARS = 20;
export function heatingRenewalSentence(renewalYear: number): string {
    return `Wir empfehlen, die Heizungsanlage nach 20 Jahren zu erneuern, für dieses Gebäude also im Jahr ${renewalYear}.`;
}

/**
 * Inline errors of the manual funding percentages (GEN-779) — exported as
 * i18next keys like the GEN-774 share bounds; the de catalog carries the
 * German wording. The order error lands on the max field.
 */
export const FUNDING_PERCENTAGE_RANGE_MSG = 'The funding percentage must be between 0 and 100.';
export const FUNDING_PERCENTAGE_ORDER_MSG = 'The maximum funding percentage must not be below the minimum.';

/**
 * Verglasung options (#30a) — values are the backend `WindowType` enum names.
 *
 * GEN-752 adds `TRIPLE_PANE` (Dreifachverglasung): a building with triple
 * glazing NEVER gets a window replacement proposal — independent of the
 * effective year and the Baujahr proxy. The GEN-750 effective-year lattice
 * (inline tri-state year > carried legacy windowReplacementYear > saniert →
 * reference year > Baujahr proxy) is untouched; the engine simply never
 * consults it for TRIPLE_PANE. Product decision from QA feedback (2026-09-16)
 * — NOT yet on the EB-validated measures sheet v2; flagged for EB validation
 * like the other post-sheet rules.
 *
 * GEN-801: rendered as buttons like the renovation items, labelled short
 * (Einfach / Doppel / Dreifach) — the row is headed "Verglasung".
 */
export const WINDOW_TYPE_OPTIONS: VariantFieldOption[] = [
    { value: 'SINGLE_PANE', labelKey: 'Single' },
    { value: 'DOUBLE_PANE', labelKey: 'Double' },
    { value: 'TRIPLE_PANE', labelKey: 'Triple' },
];

/** The Fernwärme carrier code — no heat pump is ever recommended or selectable for it. */
export const DISTRICT_HEATING_CARRIER = 'DISTRICT_HEATING';

/**
 * Energieträger options (#13) — values are the backend `EnergyCarrier` enum names.
 *
 * The six heating carriers admi supports (2026-09-29): the calibrated prognosis
 * model's carriers ("Admi_Vorprognose_Rechner" v6, Constants rows 6–11) in the
 * sheet's order — every offered carrier is one the model predicts for and has
 * an EVEBI default heat price. Biogas and Strom (Wärmepumpe) are not supported
 * and were removed from the system together with the former `HEAT_PUMP` and
 * `OTHER` ("Sonstige", with its free-text companion) — a QA stamp still naming
 * one is calculated as "no carrier" (accepted: EnSan PA is not in production,
 * only QA Variants carry them). The de catalog values are byte-copies of the
 * backend print labels (`EnergyCarrier.printLabel`), pinned by
 * steckbrief-variant-fields.test.ts. v6 split wood into Holzhackschnitzel
 * (`WOOD_CHIPS`) and Holzpellets (`WOOD_PELLETS`, the former "Holz").
 *
 * GEN-801 sheet order (pellets before chips); `LIQUEFIED_GAS` reads "Propane
 * (LPG)" in English — the German label stays the print label "Flüssiggas".
 */
export const ENERGY_CARRIER_OPTIONS: VariantFieldOption[] = [
    { value: 'NATURAL_GAS', labelKey: 'Natural gas' },
    { value: 'HEATING_OIL', labelKey: 'Heating oil' },
    { value: 'LIQUEFIED_GAS', labelKey: 'Propane (LPG)' },
    { value: 'DISTRICT_HEATING', labelKey: 'District heating' },
    { value: 'WOOD_PELLETS', labelKey: 'Wood pellets' },
    { value: 'WOOD_CHIPS', labelKey: 'Wood chips' },
];

/**
 * Beleuchtung options (#31) — values are the backend `LightingType` enum names.
 * GEN-801: LED / teilweise LED / keine LED (+ unbekannt), as buttons. Teilweise
 * LED is its own value — the backend proposes the LED measure for it like for
 * keine LED, but keeps it apart for everything that matches on the lighting
 * (the lookalike lookup to come).
 */
export const LIGHTING_TYPE_OPTIONS: VariantFieldOption[] = [
    { value: 'LED', labelKey: 'LED' },
    { value: 'PARTIAL_LED', labelKey: 'Partially LED' },
    { value: 'NO_LED', labelKey: 'No LED' },
];

/**
 * The lamp types offered before GEN-801 — retired, not removed: stamps and
 * carried values still name them, so the buttons keep their labels (one-way,
 * like the GEN-701 retired renewables). "Altbestand / gemischt" also took a
 * free-text companion (`lightingTypeOther`, #31a), which left the form with it
 * and passes through as a carried legacy value.
 */
export const RETIRED_LIGHTING_TYPE_OPTIONS: VariantFieldOption[] = [
    { value: 'FLUORESCENT_T5', labelKey: 'Fluorescent tube T5' },
    { value: 'FLUORESCENT_T8', labelKey: 'Fluorescent tube T8' },
    { value: 'INCANDESCENT', labelKey: 'Incandescent' },
    { value: 'HALOGEN', labelKey: 'Halogen' },
    { value: 'OTHER_LEGACY', labelKey: 'Legacy / mixed lighting' },
];

/**
 * Erneuerbare Energien options (#15). English codes on purpose; the backend
 * derives `hasPv` by keyword ("photovolta…"), which `PHOTOVOLTAICS` matches.
 * `HEAT_PUMP` is no longer offered here (GEN-701); see
 * [RETIRED_RENEWABLE_ENERGY_SOURCE_OPTIONS].
 */
export const RENEWABLE_ENERGY_SOURCE_OPTIONS: VariantFieldOption[] = [
    { value: 'PHOTOVOLTAICS', labelKey: 'Photovoltaics' },
    { value: 'SOLAR_THERMAL', labelKey: 'Solar thermal' },
    { value: 'BIOMASS', labelKey: 'Biomass' },
    { value: 'OTHER_RENEWABLE', labelKey: 'Other renewable source' },
];

/**
 * Retired Erneuerbare-Energien options (GEN-701): codes no longer offered for
 * NEW selection, but still present in previously saved stamps and in
 * prefill/carry-forward values. The multi-select keeps localizing them (chip
 * with the German label, still deselectable) instead of showing the raw code —
 * deselecting one is one-way, the menu never offers a retired code that is not
 * currently selected.
 *
 * `HEAT_PUMP` moved into the Energieträger select (GEN-701): heating =
 * Wärmepumpe already implies the renewable source, so stating it twice was a
 * duplication. (That carrier, later "Strom (Wärmepumpe)", is not supported
 * since 2026-09-29.)
 */
export const RETIRED_RENEWABLE_ENERGY_SOURCE_OPTIONS: VariantFieldOption[] = [
    { value: 'HEAT_PUMP', labelKey: 'Heat pump' },
];

/**
 * The value fields of the Variant form, in field-mapping order. Exactly these
 * keys travel in `CreateVariantRequest.values` / `POST /calculate` values and
 * come back from `POST /variants/prefill`.
 */
/**
 * The adjustable Prognosewerte (GEN-782): the three savings ranges of the print's
 * "Alle empfohlenen Maßnahmen" column. `systemKey`/`printedKey` name the
 * `/calculate` response fields — the engine's own range and the printed
 * (adjusted) one; `formKeyPrefix` + Low/High (+ Reason) the form keys, mirror
 * of the backend `PrognosisMetric`.
 */
export interface PrognosisMetric {
    formKeyPrefix: 'energySavingPotential' | 'co2Saving' | 'costSaving' | 'investmentCostPerSqm';
    /**
     * The backend `PrognosisMetric` enum name — how a prognosis warning names
     * the metric it questions or suppresses (`affectedMetrics`, GEN-788).
     */
    metric: PrognosisMetricKey;
    labelKey: string;
    unit: string;
    /** Upper plausibility bound (percent), null when unbounded — mirrors the backend. */
    max: number | null;
    /**
     * Whether a bound may be negative (GEN-788): only the cost saving — the
     * sheet v6's H3 is not floored at 0, a negative bound is a genuine finding
     * (NEGATIVE_LOWER_BOUND). The energy and CO₂ savings are floored at 0.
     */
    allowNegative: boolean;
    step: number;
    /** Labels of the four form keys (i18next keys), per bound. */
    fieldLabelKeys: Record<PrognosisBound, { value: string; reason: string }>;
    systemKey:
        | 'energySavingPotentialSystemRange'
        | 'co2SavingSystemRange'
        | 'costSavingSystemRange'
        | 'investmentCostPerSqmSystemRange';
    printedKey: 'energySavingPotentialRange' | 'co2SavingRange' | 'costSavingRange' | 'investmentCostPerSqmRange';
    /**
     * Advisor-only (GEN-770 D3): the model produces no system value for this metric, so it is
     * absent from the Systemprognose table. Its system twin is the lookalike aggregation stamped
     * with the Variant (GEN-700 D4) — null ("k. A.") until a lookalike search ran. Today only the
     * Investitionskosten/m².
     */
    advisorOnly?: boolean;
    /**
     * The `/calculate` field of the total DERIVED from this metric (GEN-770 D3) — the per-m² ×
     * NGF "Investitionskosten gesamt (abgeleitet)". Shown read-only under the adjustment row.
     */
    derivedTotalKey?: 'investmentCostDerivedTotalRange';
}

export const PROGNOSIS_METRICS: PrognosisMetric[] = [
    {
        formKeyPrefix: 'energySavingPotential',
        metric: 'ENERGY_SAVING_POTENTIAL',
        labelKey: 'Energy saving potential',
        fieldLabelKeys: {
            low: {
                value: 'Energy saving potential – adjusted lower bound',
                reason: 'Energy saving potential – reason lower bound',
            },
            high: {
                value: 'Energy saving potential – adjusted upper bound',
                reason: 'Energy saving potential – reason upper bound',
            },
        },
        unit: '%',
        max: 100,
        allowNegative: false,
        step: 1,
        systemKey: 'energySavingPotentialSystemRange',
        printedKey: 'energySavingPotentialRange',
    },
    {
        formKeyPrefix: 'co2Saving',
        metric: 'CO2_SAVING',
        labelKey: 'CO₂ saving',
        fieldLabelKeys: {
            low: { value: 'CO₂ saving – adjusted lower bound', reason: 'CO₂ saving – reason lower bound' },
            high: { value: 'CO₂ saving – adjusted upper bound', reason: 'CO₂ saving – reason upper bound' },
        },
        unit: 't/a',
        max: null,
        allowNegative: false,
        step: 0.1,
        systemKey: 'co2SavingSystemRange',
        printedKey: 'co2SavingRange',
    },
    {
        formKeyPrefix: 'costSaving',
        metric: 'COST_SAVING',
        labelKey: 'Cost saving',
        fieldLabelKeys: {
            low: { value: 'Cost saving – adjusted lower bound', reason: 'Cost saving – reason lower bound' },
            high: { value: 'Cost saving – adjusted upper bound', reason: 'Cost saving – reason upper bound' },
        },
        unit: '€/a',
        max: null,
        allowNegative: true,
        step: 100,
        systemKey: 'costSavingSystemRange',
        printedKey: 'costSavingRange',
    },
    {
        // GEN-770 D3: the Investitionskosten/m² — advisor-only (the model produces no per-m²
        // figure; the system twin is the stamped lookalike aggregation, GEN-700 D4). Its effective
        // range drives the derived Investitionskosten gesamt (per-m² × NGF, form only).
        formKeyPrefix: 'investmentCostPerSqm',
        metric: 'INVESTMENT_COST_PER_SQM',
        labelKey: 'Investment costs per m²',
        fieldLabelKeys: {
            low: {
                value: 'Investment costs per m² – adjusted lower bound',
                reason: 'Investment costs per m² – reason lower bound',
            },
            high: {
                value: 'Investment costs per m² – adjusted upper bound',
                reason: 'Investment costs per m² – reason upper bound',
            },
        },
        unit: '€/m²',
        max: null,
        allowNegative: false,
        step: 10,
        systemKey: 'investmentCostPerSqmSystemRange',
        printedKey: 'investmentCostPerSqmRange',
        advisorOnly: true,
        derivedTotalKey: 'investmentCostDerivedTotalRange',
    },
];

export type PrognosisBound = 'low' | 'high';
export const PROGNOSIS_BOUNDS: PrognosisBound[] = ['low', 'high'];

/** Form key of an adjusted bound, e.g. `co2SavingOverrideLow`. */
export function prognosisValueKey(metric: PrognosisMetric, bound: PrognosisBound): string {
    return `${metric.formKeyPrefix}Override${bound === 'low' ? 'Low' : 'High'}`;
}

/** Form key of an adjusted bound's reason, e.g. `co2SavingOverrideLowReason`. */
export function prognosisReasonKey(metric: PrognosisMetric, bound: PrognosisBound): string {
    return `${prognosisValueKey(metric, bound)}Reason`;
}

/** Stamped audit text only (never printed) — a generous cap against pasted essays. */
export const PROGNOSIS_REASON_MAX_LENGTH = 500;
export const PROGNOSIS_REASON_REQUIRED_MSG = 'Please give a reason for the adjustment.';
export const PROGNOSIS_VALUE_RANGE_MSG = 'Please enter a value between 0 and 100.';
export const PROGNOSIS_VALUE_NEGATIVE_MSG = 'Negative values are not plausible here.';

/**
 * Advisor-changed Zielklasse of the "Alle empfohlenen Maßnahmen" column — form
 * key of the stage (a backend `EfficiencyStage` name, null = keep the system
 * value) and of its mandatory reason (stamped, never printed). Mirror of the
 * backend `TargetClassOverride`.
 */
export const TARGET_CLASS_OVERRIDE_KEY = 'targetClassOverride';
export const TARGET_CLASS_OVERRIDE_REASON_KEY = 'targetClassOverrideReason';

/**
 * Advisor-changed Zielklasse of the "Nur Prio-Maßnahmen" column — the same pair
 * as [TARGET_CLASS_OVERRIDE_KEY] for the Prio set (stage name, null = keep the
 * engine's `priorityTargetClassSystem`; reason stamped, never printed). Mirror
 * of the backend's Prio `TargetClassOverride`.
 */
export const PRIORITY_TARGET_CLASS_OVERRIDE_KEY = 'priorityTargetClassOverride';
export const PRIORITY_TARGET_CLASS_OVERRIDE_REASON_KEY = 'priorityTargetClassOverrideReason';

/**
 * The lookalike reference context a Variant is stamped with (GEN-700 D4) — mirror of the backend
 * `LookalikeReferenceContext` form keys. Copied from the search / package-selection response, never
 * typed; user-only, so it carries into the next Variant. The backend recomputes the fingerprint
 * from the current 12 Merkmale (`lookalikeReferenceStale`). Lineage only — no lookalike figure is
 * stamped or ever placed in the Prognose.
 */
export const LOOKALIKE_REFERENCE_KEYS = {
    searchId: 'lookalikeSearchId',
    indexVersion: 'lookalikeIndexVersion',
    docIds: 'lookalikeDocIds',
    packageIds: 'lookalikePackageIds',
    fingerprint: 'lookalikeMatchFingerprint',
    /** The Gebäudevergleich itself, as JSON text — display only, shown again when the Variant is opened. */
    comparison: 'lookalikeComparison',
} as const;

/**
 * The four stages of the efficiency chart, best first — values are the backend
 * `EfficiencyStage` names, labels its print labels (the /calculate response
 * reports `targetClass` as the print label).
 */
export const TARGET_CLASS_OPTIONS: VariantFieldOption[] = [
    { value: 'GUT', labelKey: 'Gut' },
    { value: 'SOLIDE', labelKey: 'Solide' },
    { value: 'MAESSIG', labelKey: 'Mäßig' },
    { value: 'KRITISCH', labelKey: 'Kritisch' },
];

export const STECKBRIEF_VARIANT_FIELDS: SteckbriefVariantField[] = [
    // Header (#2, GEN-786): the printed "Stand" — an editable month the form
    // seeds with the current month (see initialValues in SteckbriefVariantForm).
    // User-only and NOT carried forward by the backend prefill: a new Variant
    // always starts from the month it is made in; an opened Variant shows its
    // stamped month. The backend prints it as "September 2026".
    {
        key: 'asOfDate',
        labelKey: 'Stand (month)',
        type: 'MONTH',
        section: 'HEADER',
        criticality: 'OPTIONAL',
        helpTextKey: AS_OF_DATE_HELP_TEXT_KEY,
    },

    // Title card / Eckdaten (#3–#9)
    {
        key: 'buildingFunction',
        labelKey: 'Building type',
        type: 'SELECT',
        section: 'ECKDATEN',
        criticality: 'MANDATORY',
    },
    {
        // The printed Gebäudeart: user-only free text beside the Gebäudetyp —
        // the code stays the engine input, the print shows this text. GEN-801:
        // no default any more (it seeded the picked function's register name
        // since GEN-794): the advisor writes the print name.
        key: 'printableBuildingFunction',
        labelKey: 'Printed building type',
        type: 'TEXT',
        section: 'ECKDATEN',
        criticality: 'MANDATORY',
        maxLength: PRINT_LIMIT_BUILDING_FUNCTION,
        helpTextKey: 'Printed on the Steckbrief instead of the building type.',
    },
    {
        key: 'constructionYear',
        labelKey: 'Construction year',
        type: 'YEAR',
        section: 'ECKDATEN',
        criticality: 'MANDATORY',
        startsRow: true,
    },
    {
        // GEN-801: user-only gate of the floor area — ja → the NGF is entered
        // (required), nein → the BGF instead (required; the engine derives NGF =
        // 0.85 × BGF). Neither renders until it is answered; the hidden one is
        // nulled in every payload. The form seeds it from the data when the
        // prefill carries no answer (an NGF → ja, else a BGF → nein).
        key: 'netFloorAreaKnown',
        startsRow: true,
        labelKey: 'Is NGF known?',
        type: 'TRI_STATE',
        section: 'ECKDATEN',
        criticality: 'MANDATORY',
        triStateFlavor: 'BOOLEAN',
        explicitAnswer: true,
    },
    {
        key: 'netFloorArea',
        labelKey: 'Net floor area (NGF, m²)',
        type: 'NUMBER',
        section: 'ECKDATEN',
        criticality: 'MANDATORY',
        conditionalOn: { field: 'netFloorAreaKnown', value: true },
    },
    {
        // User-only engine input (mapping "Bruttogrundfläche" row): input of the
        // NGF = 0.85 × BGF derivation, consumed by /calculate. Not printed.
        // Asked (and required) while the NGF is not known.
        key: 'grossFloorArea',
        labelKey: 'Gross floor area (BGF, m²)',
        type: 'NUMBER',
        section: 'ECKDATEN',
        criticality: 'MANDATORY',
        conditionalOn: { field: 'netFloorAreaKnown', value: false },
        helpTextKey:
            'Fallback input: NGF is derived as 0.85 × BGF when the net floor area is missing. In a SALES Variant without NGF and BGF, both are estimated from the 3D building model.',
    },
    // Building-surface figures (GEN-773 / GEN-798), in derivation order — all
    // displayOnly: NOT editable, shown only in "Assumptions & derived values".
    // footprintArea / rawSurfaceArea / realSurfaceArea / adjacentBuildingCount
    // are prefilled from the building geometry via the Data Catalog
    // (`building_api` leaves — data-products derives them from the CityGML
    // model: raw = the LoD2 wall surface as modelled, real = raw minus the
    // party walls shared with attached neighbours; both fall back to the
    // footprint prism without wall surfaces) and travel to /calculate and the
    // stamp unchanged. Inputs of the investment cost estimate (GEN-780:
    // roof/basement per footprint m², facade insulation per OPAQUE facade m²,
    // windows and sun protection per window m²). Keys byte-match the backend
    // VariantFieldMapping.
    {
        key: 'footprintArea',
        maxDecimals: DERIVED_NUMBER_MAX_DECIMALS,
        labelKey: 'Building footprint (m²)',
        type: 'NUMBER',
        section: 'ECKDATEN',
        criticality: 'OPTIONAL',
        displayOnly: true,
    },
    {
        key: 'rawSurfaceArea',
        maxDecimals: DERIVED_NUMBER_MAX_DECIMALS,
        labelKey: 'Raw wall surface area (m²)',
        type: 'NUMBER',
        section: 'ECKDATEN',
        criticality: 'OPTIONAL',
        displayOnly: true,
    },
    {
        key: 'realSurfaceArea',
        maxDecimals: DERIVED_NUMBER_MAX_DECIMALS,
        labelKey: 'Real wall surface area (m²)',
        type: 'NUMBER',
        section: 'ECKDATEN',
        criticality: 'OPTIONAL',
        displayOnly: true,
    },
    {
        // GEN-774: user-only model parameter (no catalog key) of the
        // window-area derivation — the `/calculate` pipeline computes
        // windowSurfaceArea = realSurfaceArea × share / 100 from the
        // SUBMITTED values. GEN-802: the form seeds the model default of the
        // Gebäudetyp (#3) from the assumption defaults whenever the prefill
        // carries nothing, and the value follows #3 until the advisor edits it
        // (see steckbrief-assumption-defaults). A save stamps it
        // DEFAULT while it equals that default, else USER_PROVIDED.
        // Plausibility 0 < share ≤ 100 is enforced by the values schema below;
        // required on FINAL (GEN-801).
        key: 'windowAreaSharePercent',
        labelKey: 'Window area share (%)',
        type: 'NUMBER',
        section: 'ECKDATEN',
        criticality: 'MANDATORY',
        helpTextKey: WINDOW_AREA_SHARE_HELP_TEXT_KEY,
    },
    {
        // Window surface area = real × share (GEN-774), derived by /calculate:
        // an ENGINE_ECHO key the form always submits as null — the backend's
        // explicit leg only serves stamps saved while the field was editable.
        // The save payload stamps the echoed value, so generation replays
        // exactly what the section showed.
        key: 'windowSurfaceArea',
        maxDecimals: DERIVED_NUMBER_MAX_DECIMALS,
        labelKey: 'Window surface area (m²)',
        type: 'NUMBER',
        section: 'ECKDATEN',
        criticality: 'OPTIONAL',
        displayOnly: true,
    },
    {
        // The OPAQUE facade = real − window (GEN-798), derived by /calculate —
        // the windowSurfaceArea mechanics verbatim. Priced for the Fassadendämmung.
        key: 'facadeSurfaceArea',
        maxDecimals: DERIVED_NUMBER_MAX_DECIMALS,
        labelKey: 'Facade surface area (m²)',
        type: 'NUMBER',
        section: 'ECKDATEN',
        criticality: 'OPTIONAL',
        displayOnly: true,
    },
    {
        key: 'adjacentBuildingCount',
        labelKey: 'Adjacent buildings',
        type: 'NUMBER',
        section: 'ECKDATEN',
        criticality: 'OPTIONAL',
        displayOnly: true,
    },
    {
        // QA feedback round: user-only model parameter scaling every
        // investment cost estimate (all measures, incl. PV) by factor / 100.
        // Seeded DEFAULT_INVESTMENT_COST_FACTOR_PERCENT when absent (see
        // initialValues in SteckbriefVariantForm), 1–500 enforced by the
        // values schema. Rendered next to the KPI investment totals.
        key: 'investmentCostFactorPercent',
        labelKey: 'Investment cost factor (%)',
        type: 'NUMBER',
        section: 'INVESTMENT',
        criticality: 'OPTIONAL',
        helpTextKey: INVESTMENT_COST_FACTOR_HELP_TEXT_KEY,
    },
    {
        key: 'inUse',
        labelKey: 'In use',
        type: 'TRI_STATE',
        section: 'ECKDATEN',
        criticality: 'MANDATORY',
        triStateFlavor: 'YES_NO',
        explicitAnswer: true,
    },
    {
        key: 'monumentProtection',
        labelKey: 'Monument protection',
        type: 'TRI_STATE',
        section: 'ECKDATEN',
        criticality: 'MANDATORY',
        triStateFlavor: 'YES_NO',
        explicitAnswer: true,
    },
    {
        key: 'owner',
        labelKey: 'Owner of the building',
        type: 'TEXT',
        section: 'ECKDATEN',
        criticality: 'MANDATORY',
        maxLength: PRINT_LIMIT_OWNER,
        helpTextKey: OWNER_HELP_TEXT_KEY,
    },
    {
        // Prefilled from the REA's main address — the backend strips the
        // geocoder's trailing ", Germany" (GEN-801).
        key: 'address',
        labelKey: 'Building address',
        type: 'TEXT',
        section: 'ECKDATEN',
        criticality: 'MANDATORY',
        maxLength: PRINT_LIMIT_ADDRESS,
    },

    // Building photo (#12)
    {
        key: 'buildingPhoto',
        labelKey: 'Building photo',
        type: 'ATTACHMENT',
        section: 'BUILDING_PHOTO',
        criticality: 'IMPORTANT',
    },

    // Energie & Wärme (#13–#19)
    {
        // GEN-801: "unknown" stays the dropdown's empty entry, but the FINAL
        // Steckbrief needs a real carrier.
        key: 'heatingEnergyCarrier',
        labelKey: 'Energy carrier',
        type: 'SELECT',
        section: 'ENERGIE_WAERME',
        criticality: 'MANDATORY',
        options: ENERGY_CARRIER_OPTIONS,
    },
    {
        // QA feedback round: user-only gate "Is the heating construction year
        // known?" — ja asks for the year (required on FINAL), nein hides it (the
        // backend then ignores a submitted year and derives or assumes one).
        // A ja/nein gate like "Is NGF known?" (GEN-801 mechanics): no default
        // selection, except the seed — ja when the prefill carries a heating
        // year but no answer (see seedHeatingYearGate).
        key: 'heatingConstructionYearKnown',
        labelKey: 'Is the heating construction year known?',
        type: 'TRI_STATE',
        section: 'ENERGIE_WAERME',
        criticality: 'MANDATORY',
        triStateFlavor: 'BOOLEAN',
        explicitAnswer: true,
        // The gate on the left, the year right of it.
        startsRow: true,
    },
    {
        key: 'heatingConstructionYear',
        labelKey: 'Heating construction year',
        type: 'YEAR',
        section: 'ENERGIE_WAERME',
        criticality: 'MANDATORY',
        conditionalOn: { field: 'heatingConstructionYearKnown', value: true },
    },
    {
        // GEN-801: user-only gate of the sources — ja/nein/unbekannt, no
        // default selection. Only ja shows the sources (then at least one is
        // required); otherwise they are nulled in every payload. Seeded ja when
        // the prefill carries sources but no answer. Not printed.
        key: 'existingRenewableEnergy',
        startsRow: true,
        labelKey: 'Existing renewable energy?',
        type: 'TRI_STATE',
        section: 'ENERGIE_WAERME',
        criticality: 'MANDATORY',
        triStateFlavor: 'YES_NO',
        explicitAnswer: true,
    },
    {
        key: 'renewableEnergySources',
        startsRow: true,
        labelKey: 'Renewable energy sources',
        type: 'MULTI_SELECT',
        section: 'ENERGIE_WAERME',
        criticality: 'MANDATORY',
        options: RENEWABLE_ENERGY_SOURCE_OPTIONS,
        retiredOptions: RETIRED_RENEWABLE_ENERGY_SOURCE_OPTIONS,
        conditionalOn: { field: 'existingRenewableEnergy', value: true },
    },
    {
        // GEN-739: user-only free text specifying the "other" renewable source —
        // key mirrors the backend VariantFieldMapping companion key. Multi-select
        // gate: visible while OTHER_RENEWABLE is among the selected values (and
        // the sources render at all — see fieldVisibleIn).
        key: 'renewableEnergySourcesOther',
        startsRow: true,
        labelKey: 'Other renewable source — please specify',
        type: 'TEXT',
        section: 'ENERGIE_WAERME',
        criticality: 'MANDATORY',
        conditionalOn: { field: 'renewableEnergySources', value: 'OTHER_RENEWABLE' },
        maxLength: PRINT_LIMIT_ENERGY_OTHER_TEXT,
    },
    {
        // GEN-744: one year-tagged consumption entry per carrier replaces the
        // former heatConsumptionY1/Y2 scalar pair — the value is a
        // `ConsumptionEntry[]` so it says WHICH year it measured.
        key: 'heatConsumption',
        // GEN-786: heat and electricity consumption share the LEFT column, one
        // below the other.
        startsRow: true,
        labelKey: 'Heat consumption (kWh/a)',
        type: 'CONSUMPTION_ENTRIES',
        section: 'ENERGIE_WAERME',
        criticality: 'MANDATORY',
    },

    {
        // GEN-744: replaces the former electricityConsumptionY1/Y2 scalar pair.
        key: 'electricityConsumption',
        startsRow: true,
        labelKey: 'Electricity consumption (kWh/a)',
        type: 'CONSUMPTION_ENTRIES',
        section: 'ENERGIE_WAERME',
        criticality: 'MANDATORY',
    },
    // GEN-788 (sheet v6 H3): the two tariffs of the cost-saving prognosis, on
    // their own row under the consumptions (heat | electricity). User-only,
    // OPTIONAL, printed, carried forward like every user-only field and
    // stamped only when entered: empty = the EVEBI default (the carrier's heat
    // price, Constants E6:E13 / the electricity price E13), shown as the
    // placeholder from the /calculate echo. Plausibility 0 < price ≤ 1 €/kWh
    // is enforced by the values schema below.
    {
        key: 'heatPrice',
        startsRow: true,
        labelKey: 'Heat price',
        type: 'NUMBER',
        section: 'ENERGIE_WAERME',
        criticality: 'OPTIONAL',
        helpTextKey: HEAT_PRICE_HELP_TEXT_KEY,
        decimalInput: true,
    },
    {
        key: 'electricityPrice',
        labelKey: 'Electricity price',
        type: 'NUMBER',
        section: 'ENERGIE_WAERME',
        criticality: 'OPTIONAL',
        helpTextKey: ELECTRICITY_PRICE_HELP_TEXT_KEY,
        decimalInput: true,
    },
    // Sanierungshistorie (#27–#35a, incl. the user-only engine inputs #30a/#30b/#35a)
    {
        key: 'roofRenovation',
        labelKey: 'Roof insulation',
        type: 'TRI_STATE',
        section: 'SANIERUNGSHISTORIE',
        criticality: 'MANDATORY',
        triStateFlavor: 'RENOVATION',
        explicitAnswer: true,
        // GEN-748: the renovation year travels inline in the tri-state value.
        renovationYearInput: true,
    },
    {
        // GEN-745: user-only short note, printed in the Steckbrief UNDER the
        // item row — the generalization of waterLeakageLocation's under-item
        // text. Key mirrors the backend VariantFieldMapping. Deliberately NOT
        // conditional: a note applies whatever the tri-state says.
        key: 'roofRenovationNote',
        labelKey: 'Roof insulation note',
        type: 'TEXT',
        section: 'SANIERUNGSHISTORIE',
        criticality: 'OPTIONAL',
        maxLength: RENOVATION_NOTE_MAX_LENGTH,
        helpTextKey: RENOVATION_NOTE_HELP_TEXT_KEY,
    },
    {
        key: 'facadeRenovation',
        labelKey: 'Facade insulation',
        type: 'TRI_STATE',
        section: 'SANIERUNGSHISTORIE',
        criticality: 'MANDATORY',
        triStateFlavor: 'RENOVATION',
        explicitAnswer: true,
        // GEN-748: the renovation year travels inline in the tri-state value.
        renovationYearInput: true,
    },
    {
        // GEN-745: printed under the item like waterLeakageLocation (see roofRenovationNote).
        key: 'facadeRenovationNote',
        labelKey: 'Facade insulation note',
        type: 'TEXT',
        section: 'SANIERUNGSHISTORIE',
        criticality: 'OPTIONAL',
        maxLength: RENOVATION_NOTE_MAX_LENGTH,
        helpTextKey: RENOVATION_NOTE_HELP_TEXT_KEY,
    },
    {
        key: 'basementInsulation',
        labelKey: 'Basement insulation',
        type: 'TRI_STATE',
        section: 'SANIERUNGSHISTORIE',
        criticality: 'MANDATORY',
        triStateFlavor: 'RENOVATION',
        explicitAnswer: true,
        // GEN-748: the renovation year travels inline in the tri-state value.
        renovationYearInput: true,
    },
    {
        // GEN-745: printed under the item like waterLeakageLocation (see roofRenovationNote).
        key: 'basementInsulationNote',
        labelKey: 'Basement insulation note',
        type: 'TEXT',
        section: 'SANIERUNGSHISTORIE',
        criticality: 'OPTIONAL',
        maxLength: RENOVATION_NOTE_MAX_LENGTH,
        helpTextKey: RENOVATION_NOTE_HELP_TEXT_KEY,
    },
    {
        // GEN-750: the window collects its renovation year like every other
        // Sanierungshistorie item — inline in the saniert tri-state (GEN-748).
        // The standalone `windowReplacementYear` field (#30b) is gone from the
        // form model: it predated GEN-748 (tri-states were boolean-only and the
        // window measure rule needed an effective year), so it had a dedicated
        // input; the inline-year mechanism made that field redundant. The KEY
        // stays in the backend VariantFieldMapping/formSchema — legacy stamps
        // keep validating and carrying forward (see the carried-legacy note in
        // the model header) — and the engine's window-year precedence is:
        // inline numeric tri-state value > carried legacy windowReplacementYear
        // > saniert (true) → reference year > Baujahr proxy.
        key: 'windowReplacement',
        labelKey: 'Window replacement',
        type: 'TRI_STATE',
        section: 'SANIERUNGSHISTORIE',
        criticality: 'MANDATORY',
        triStateFlavor: 'RENOVATION',
        explicitAnswer: true,
        renovationYearInput: true,
    },
    {
        // GEN-745: printed under the item like waterLeakageLocation (see roofRenovationNote).
        key: 'windowReplacementNote',
        labelKey: 'Window replacement note',
        type: 'TEXT',
        section: 'SANIERUNGSHISTORIE',
        criticality: 'OPTIONAL',
        maxLength: RENOVATION_NOTE_MAX_LENGTH,
        helpTextKey: RENOVATION_NOTE_HELP_TEXT_KEY,
    },
    {
        key: 'windowType',
        labelKey: 'Glazing',
        type: 'SELECT',
        section: 'SANIERUNGSHISTORIE',
        criticality: 'MANDATORY',
        options: WINDOW_TYPE_OPTIONS,
        // GEN-801: buttons like the renovation items, no default selection.
        buttons: true,
        explicitAnswer: true,
        // GEN-751: no helper text — the proposal rule (Einfachverglasung → always,
        // Doppelverglasung → effective year ≤ 2005 or no year known at all,
        // Dreifachverglasung → never, GEN-752) lives in the field mapping and the
        // measures sheet, not in the form.
    },
    {
        // GEN-786 layout: right of Glazing (both window/facade facts), so the
        // lighting and water-leakage pairs below line up left | right.
        key: 'sunProtection',
        labelKey: 'Sun protection',
        type: 'TRI_STATE',
        section: 'SANIERUNGSHISTORIE',
        criticality: 'MANDATORY',
        triStateFlavor: 'YES_NO',
        explicitAnswer: true,
        helpTextKey: 'Engine input of the sun-protection measure rule — assumed missing when unknown.',
    },

    // GEN-753: `electricalInstallationRenovation` (#34) and `fullRenovation`
    // (#35) are REMOVED from the form — neither is printed in template v1 and
    // neither feeds the engines (collected-and-stamped only). Old stamps that
    // still carry the keys render through the legacy label fallback
    // ([LEGACY_STAMP_FIELD_LABEL_KEYS]); should the backend keep the keys in
    // its `VariantFieldMapping`, prefilled values simply carry forward through
    // the generic GEN-750 pass-through ([carriedLegacyKeys]).
    {
        key: 'lightingType',
        startsRow: true,
        labelKey: 'Lighting',
        type: 'SELECT',
        section: 'SANIERUNGSHISTORIE',
        criticality: 'MANDATORY',
        options: LIGHTING_TYPE_OPTIONS,
        retiredOptions: RETIRED_LIGHTING_TYPE_OPTIONS,
        // GEN-801: buttons like the renovation items, no default selection.
        buttons: true,
        explicitAnswer: true,
    },
    {
        // GEN-745: printed under the item like waterLeakageLocation (see
        // roofRenovationNote). GEN-786 layout: right of the lighting buttons.
        key: 'lightingTypeNote',
        labelKey: 'Lighting note',
        type: 'TEXT',
        section: 'SANIERUNGSHISTORIE',
        criticality: 'OPTIONAL',
        maxLength: RENOVATION_NOTE_MAX_LENGTH,
        helpTextKey: RENOVATION_NOTE_HELP_TEXT_KEY,
    },
    // GEN-801: `lightingTypeOther` (#31a, the free text of "Altbestand /
    // gemischt") left the form with the lamp types — a stamped text passes
    // through as a carried legacy value (carriedLegacyKeys).
    {
        key: 'waterLeakage',
        // GEN-786: left column; the conditional note appears right of it.
        startsRow: true,
        labelKey: 'Water leakage',
        type: 'TRI_STATE',
        section: 'SANIERUNGSHISTORIE',
        criticality: 'IMPORTANT',
        triStateFlavor: 'YES_NO',
    },
    {
        key: 'waterLeakageLocation',
        // Relabeled "Water leakage note" (GEN-786) — the key stays: it is the
        // backend contract and the READI code's catalog leaf.
        labelKey: 'Water leakage note',
        type: 'TEXT',
        section: 'SANIERUNGSHISTORIE',
        criticality: 'OPTIONAL',
        conditionalOn: { field: 'waterLeakage', value: true },
    },
    // Hinweise der Kommune (#36)
    {
        key: 'municipalityNotes',
        // GEN-786: notes FROM the municipality ABOUT the building — not notes
        // about the municipality as a customer.
        labelKey: 'Notes from the municipality about the building',
        helpTextKey: MUNICIPALITY_NOTES_HELP_TEXT_KEY,
        type: 'MULTILINE_TEXT',
        section: 'HINWEISE_KOMMUNE',
        criticality: 'MANDATORY',
        maxLength: PRINT_LIMIT_MUNICIPALITY_NOTES,
    },

    // Förderung (#43a–#43c, GEN-779) — the MVP prints NO funding-engine output:
    // ops enter the printed "Prognostizierte Förderquote" range and its Hinweis
    // by hand (user-only, carried forward from the previous Variant). The
    // former funding gates (#46–#48: budgetConsolidation, nonEconomicUse,
    // klimaKommune) left the form with the engine output they fed — the backend
    // keeps them in its VariantFieldMapping, so their values pass through as
    // carried legacy values (see carriedLegacyKeys) until a funding calculation
    // returns.
    {
        key: 'fundingPercentageMin',
        labelKey: 'Funding percentage (min)',
        type: 'NUMBER',
        section: 'FOERDERUNG',
        criticality: 'MANDATORY',
        helpTextKey: FUNDING_PERCENTAGE_HELP_TEXT_KEY,
    },
    {
        key: 'fundingPercentageMax',
        labelKey: 'Funding percentage (max)',
        type: 'NUMBER',
        section: 'FOERDERUNG',
        criticality: 'MANDATORY',
        helpTextKey: FUNDING_PERCENTAGE_HELP_TEXT_KEY,
    },
    {
        key: 'fundingComment',
        labelKey: 'Funding comment',
        type: 'MULTILINE_TEXT',
        section: 'FOERDERUNG',
        criticality: 'MANDATORY',
        maxLength: PRINT_LIMIT_FUNDING_COMMENT,
        helpTextKey: FUNDING_COMMENT_HELP_TEXT_KEY,
    },

    // Bottom section (#50) — printed for BOTH kinds since layout v4 (GEN-775;
    // before: FINAL only, replacing the SALES CTA). GEN-801: the form offers the
    // sheet's two standard sentences under their conditions (see
    // extraInformationSuggestions in SteckbriefVariantForm).
    {
        key: 'extraInformation',
        labelKey: 'Additional information from admi',
        type: 'MULTILINE_TEXT',
        section: 'BOTTOM_SECTION',
        criticality: 'MANDATORY',
        maxLength: PRINT_LIMIT_EXTRA_INFORMATION,
        helpTextKey: EXTRA_INFORMATION_HELP_TEXT_KEY,
    },

    // Advisor-adjusted Prognosewerte (GEN-782): one NUMBER per bound of the three
    // "Alle empfohlenen Maßnahmen" savings ranges (null = keep the system value)
    // and one mandatory reason per adjusted bound. User-only; /calculate applies
    // an adjusted bound over the engine's, so preview, save and generation print
    // the adjusted range. The reasons are stamped, never printed.
    ...PROGNOSIS_METRICS.flatMap((metric) =>
        PROGNOSIS_BOUNDS.map(
            (bound): SteckbriefVariantField => ({
                key: prognosisValueKey(metric, bound),
                labelKey: metric.fieldLabelKeys[bound].value,
                type: 'NUMBER',
                section: 'PROGNOSE',
                criticality: 'OPTIONAL',
            }),
        ),
    ),
    ...PROGNOSIS_METRICS.flatMap((metric) =>
        PROGNOSIS_BOUNDS.map(
            (bound): SteckbriefVariantField => ({
                key: prognosisReasonKey(metric, bound),
                labelKey: metric.fieldLabelKeys[bound].reason,
                type: 'TEXT',
                section: 'PROGNOSE',
                criticality: 'OPTIONAL',
                maxLength: PROGNOSIS_REASON_MAX_LENGTH,
            }),
        ),
    ),

    // Advisor-changed Zielklasse: the stage the comparison table prints for "Alle
    // empfohlenen Maßnahmen" (null = keep the system value) plus its mandatory
    // reason. User-only; /calculate prints the changed stage under targetClass,
    // the reason is stamped, never printed. Edited in the Prognose panel.
    {
        key: TARGET_CLASS_OVERRIDE_KEY,
        labelKey: 'Target class – changed',
        type: 'SELECT',
        section: 'PROGNOSE',
        criticality: 'OPTIONAL',
        options: TARGET_CLASS_OPTIONS,
    },
    {
        key: TARGET_CLASS_OVERRIDE_REASON_KEY,
        labelKey: 'Target class – reason',
        type: 'TEXT',
        section: 'PROGNOSE',
        criticality: 'OPTIONAL',
        maxLength: PROGNOSIS_REASON_MAX_LENGTH,
    },
    // The same for the "Nur Prio-Maßnahmen" column (printed under priorityTargetClass).
    {
        key: PRIORITY_TARGET_CLASS_OVERRIDE_KEY,
        labelKey: 'Target class (priority measures only) – changed',
        type: 'SELECT',
        section: 'PROGNOSE',
        criticality: 'OPTIONAL',
        options: TARGET_CLASS_OPTIONS,
    },
    {
        key: PRIORITY_TARGET_CLASS_OVERRIDE_REASON_KEY,
        labelKey: 'Target class (priority measures only) – reason',
        type: 'TEXT',
        section: 'PROGNOSE',
        criticality: 'OPTIONAL',
        maxLength: PROGNOSIS_REASON_MAX_LENGTH,
    },

    // The lookalike reference context (GEN-700 D4): written by the Gebäudevergleich from the
    // search response, never rendered as inputs. Lists are positional pairs (doc id ↔ package id).
    {
        key: LOOKALIKE_REFERENCE_KEYS.searchId,
        labelKey: 'Lookalike search ID',
        type: 'TEXT',
        section: 'LOOKALIKE',
        criticality: 'OPTIONAL',
    },
    {
        key: LOOKALIKE_REFERENCE_KEYS.indexVersion,
        labelKey: 'Lookalike index version',
        type: 'TEXT',
        section: 'LOOKALIKE',
        criticality: 'OPTIONAL',
    },
    {
        key: LOOKALIKE_REFERENCE_KEYS.docIds,
        labelKey: 'Lookalike reports',
        type: 'MULTI_SELECT',
        section: 'LOOKALIKE',
        criticality: 'OPTIONAL',
    },
    {
        key: LOOKALIKE_REFERENCE_KEYS.packageIds,
        labelKey: 'Lookalike measure packages',
        type: 'MULTI_SELECT',
        section: 'LOOKALIKE',
        criticality: 'OPTIONAL',
    },
    {
        key: LOOKALIKE_REFERENCE_KEYS.fingerprint,
        labelKey: 'Lookalike fingerprint',
        type: 'TEXT',
        section: 'LOOKALIKE',
        criticality: 'OPTIONAL',
    },
    {
        key: LOOKALIKE_REFERENCE_KEYS.comparison,
        labelKey: 'Lookalike comparison',
        type: 'TEXT',
        section: 'LOOKALIKE',
        criticality: 'OPTIONAL',
    },
];

export const STECKBRIEF_VARIANT_FIELD_KEYS: string[] = STECKBRIEF_VARIANT_FIELDS.map((field) => field.key);

const FIELDS_BY_KEY: Map<string, SteckbriefVariantField> = new Map(
    STECKBRIEF_VARIANT_FIELDS.map((field) => [field.key, field]),
);

export function variantFieldByKey(key: string): SteckbriefVariantField | undefined {
    return FIELDS_BY_KEY.get(key);
}

/** Rounds a number to at most [decimals] decimals; anything else passes through. */
export function roundToDecimals(value: unknown, decimals: number): unknown {
    if (typeof value !== 'number' || !Number.isFinite(value)) return value;
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
}

/**
 * The prefill with every DERIVED number ([SteckbriefVariantField.maxDecimals])
 * rounded (GEN-786). Applied where the form seeds — to the value AND to the
 * source-badge baseline alike, so an opened Variant whose stamp predates the
 * backend rounding shows e.g. 802.36 instead of 802.3599999999999 without its
 * badge flipping to "User provided".
 */
export function roundDerivedPrefill(prefill: VariantPrefillDto): VariantPrefillDto {
    const fields = { ...prefill.fields };
    for (const field of STECKBRIEF_VARIANT_FIELDS) {
        const entry = fields[field.key];
        if (field.maxDecimals === undefined || entry === undefined) continue;
        fields[field.key] = { ...entry, value: roundToDecimals(entry.value, field.maxDecimals) };
    }
    return { ...prefill, fields };
}

/**
 * The prefill as the form seeds and merges it: derived numbers rounded
 * ([roundDerivedPrefill]), a Gebäudetyp outside
 * [STECKBRIEF_BUILDING_FUNCTION_CODES] dropped — the form preselects the
 * REA's (or a carried) function only when it is in the list; otherwise ops pick
 * — and every `DEFAULT` value emptied (GEN-802, an opened Variant's
 * stamp): a model default is never carried, the form re-seeds the current one.
 */
export function normalizeVariantPrefill(prefill: VariantPrefillDto): VariantPrefillDto {
    const rounded = roundDerivedPrefill(prefill);
    const normalized = { ...rounded, fields: dropAssumptionDefaults(rounded.fields) };
    const entry = normalized.fields['buildingFunction'];
    if (entry?.value == null || STECKBRIEF_BUILDING_FUNCTION_CODES.includes(String(entry.value))) return normalized;
    return { ...normalized, fields: { ...normalized.fields, buildingFunction: { value: null, source: null } } };
}

/**
 * Prefill keys that never enter the form values or the save payload:
 * `steckbriefCode` (#1) is a computed placeholder, not a form field — see the
 * model header comment. `heatPumpPower` (the hourly heat consumption, GEN-780)
 * is backend-only since it left the form: the investment estimate always
 * derives it during the calculation (newest heat consumption ÷ the building
 * type's yearly heating hours), so an explicit value in an older stamp is
 * dropped instead of carried — it would otherwise keep overriding the
 * derivation with no control left to change it.
 */
export const PREFILL_DROPPED_KEYS: string[] = ['steckbriefCode', 'heatPumpPower'];

/**
 * The carried legacy keys among `prefillFieldKeys` (GEN-750): every prefill
 * key that is neither a field of this model nor deliberately dropped
 * ([PREFILL_DROPPED_KEYS]). The backend `VariantFieldMapping` still owns these
 * keys (today: `windowReplacementYear`, #30b — see the `windowReplacement`
 * entry — the GEN-779 funding gates #46–#48 and, since GEN-801,
 * `lightingTypeOther`, #31a), so their values must flow through the form VERBATIM — never rendered,
 * never edited, never nulled — into `/calculate` and the save payload.
 */
export function carriedLegacyKeys(prefillFieldKeys: string[]): string[] {
    return prefillFieldKeys.filter((key) => !FIELDS_BY_KEY.has(key) && !PREFILL_DROPPED_KEYS.includes(key));
}

/**
 * i18next label keys of RETIRED form keys that legacy stamps still contain
 * (GEN-750): the calculation messages label them like live fields instead of
 * showing the raw key. de catalog: "Jahr Fenstertausch",
 * "Elektroinstallation", "Komplettsanierung".
 */
export const LEGACY_STAMP_FIELD_LABEL_KEYS: Record<string, string> = {
    // #30b — the standalone window year field, folded into the
    // `windowReplacement` tri-state's inline year by GEN-750.
    windowReplacementYear: 'Window replacement year',
    // #34/#35 — removed from the form by GEN-753 (never printed, never fed the
    // engines); old stamps keep their legible labels through this fallback.
    electricalInstallationRenovation: 'Electrical installation',
    fullRenovation: 'Full renovation',
    // #46–#48 — the funding gates, removed from the form by GEN-779 with the
    // engine output they fed; the backend still owns (and prefills) the keys.
    budgetConsolidation: 'Budget consolidation',
    nonEconomicUse: 'Non-economic use',
    klimaKommune: 'Klima-Kommune',
    // GEN-780 — the hourly heat consumption, removed from the form (backend-only,
    // derived during the calculation); older stamps and the
    // HEAT_PUMP_POWER_ESTIMATED assumption keep a legible label.
    heatPumpPower: 'Hourly heat consumption (kW)',
    // #31a — the "Altbestand / gemischt" free text, removed from the form by
    // GEN-801 with the lamp types; the backend still owns (and prefills) the key.
    lightingTypeOther: 'Legacy / mixed lighting — please specify',
};

/** Section header i18next keys, in template order. */
export const VARIANT_FORM_SECTIONS: { section: VariantFormSection; labelKey: string }[] = [
    { section: 'HEADER', labelKey: 'Document' },
    { section: 'ECKDATEN', labelKey: 'Key data' },
    { section: 'BUILDING_PHOTO', labelKey: 'Building photo' },
    { section: 'ENERGIE_WAERME', labelKey: 'Energy & heat' },
    { section: 'SANIERUNGSHISTORIE', labelKey: 'Renovation history' },
    { section: 'HINWEISE_KOMMUNE', labelKey: 'Notes from the municipality' },
    { section: 'FOERDERUNG', labelKey: 'Funding' },
    // Printed for both kinds since layout v4 (GEN-775) — the header names the
    // print section, like the field it holds.
    { section: 'BOTTOM_SECTION', labelKey: 'Additional information from admi' },
];

/**
 * The heating systems the engine can auto-propose: since GEN-780 (Bio-Gas no
 * longer offered) the heating measure ALWAYS proposes the heat pump; the
 * remaining offered `RecommendedHeatingSystemKey` values are advisor-selectable
 * only.
 */
export const AUTO_PROPOSABLE_HEATING_SYSTEMS: RecommendedHeatingSystemKey[] = ['HEAT_PUMP'];

/**
 * Heating systems RETIRED from the offer (GEN-780: admi no longer offers
 * Bio-Gas) — the GEN-777 retirement pattern: the engine never proposes them
 * and the form does not offer them as an advisor override, but the enum values
 * and labels are KEPT — a legacy stamp whose override is one of them still
 * renders it as a selected "no longer offered" option (deselecting it is
 * one-way), and saves it back verbatim while untouched.
 */
export const RETIRED_HEATING_SYSTEMS: RecommendedHeatingSystemKey[] = [
    'BIO_GAS',
    'BIO_GAS_WITH_SOLAR_THERMAL',
    'HEAT_PUMP_WITH_BIO_GAS',
];

/**
 * All heating-system options with their i18next label keys (GEN-758) — the
 * retired ones included, legacy display needs their labels. The de catalog
 * values are byte-copies of the backend German print labels
 * (`RecommendedHeatingSystem.chipLabel` in the backend `ensan` module's
 * `Measure.kt`) — no compiler enforces that cross-repo match, so
 * steckbrief-variant-fields.test.ts pins the catalog against those labels.
 * The override select offers [OFFERED_HEATING_SYSTEM_OPTIONS].
 */
export const HEATING_SYSTEM_OPTIONS: { value: RecommendedHeatingSystemKey; labelKey: string }[] = [
    { value: 'HEAT_PUMP', labelKey: 'Heat pump' },
    { value: 'BIO_GAS', labelKey: 'Bio-gas' },
    { value: 'BIO_GAS_WITH_SOLAR_THERMAL', labelKey: 'Bio-gas with solar thermal' },
    { value: 'HEAT_PUMP_WITH_BIO_GAS', labelKey: 'Heat pump with bio-gas' },
    { value: 'BIOMASS', labelKey: 'Biomass' },
    { value: 'BIOMASS_WITH_SOLAR_THERMAL', labelKey: 'Biomass with solar thermal' },
    { value: 'ELECTRIC_HEATING', labelKey: 'Electric heating' },
];

/**
 * The heating systems the advisor override offers (GEN-780): HEAT_PUMP,
 * BIOMASS, BIOMASS_WITH_SOLAR_THERMAL, ELECTRIC_HEATING — every option minus
 * [RETIRED_HEATING_SYSTEMS].
 */
export const OFFERED_HEATING_SYSTEM_OPTIONS = HEATING_SYSTEM_OPTIONS.filter(
    (option) => !RETIRED_HEATING_SYSTEMS.includes(option.value),
);

/** UI label of a heating-system code in the active language ('–' for null, raw code for unknown ones). */
export function heatingSystemLabel(t: TFunction, value: string | null): string {
    if (value === null) return '–';
    const option = HEATING_SYSTEM_OPTIONS.find((o) => o.value === value);
    return option === undefined ? value : t(option.labelKey);
}

/**
 * German print labels of all measures (mirrors the backend `Measure` enum's
 * `chipLabel`s — `Measure.kt` in the backend `ensan` module). Since GEN-758
 * the UI renders measures through `measureLabel` instead; this map REMAINS the
 * backend print-label contract, used to match the `/calculate` echo (the
 * German `priorityMeasures` strings) and as the source the de catalog entries
 * of `MEASURE_LABEL_KEYS` are pinned against.
 *
 * GEN-777 renamed the labels product-wide (e.g. HEATING_RENEWAL prints as
 * "Wärmepumpe" — a LABEL change only; since GEN-780 the measure also always
 * proposes the heat pump, Bio-Gas is no longer offered). The RETIRED measures
 * keep their (renamed) labels too — legacy stamps must render them.
 */
export const MEASURE_CHIP_LABELS: Record<MeasureKey, string> = {
    ROOF_TOP_CEILING_INSULATION: 'Dachdämmung',
    FACADE_INSULATION: 'Fassadendämmung',
    BASEMENT_CEILING_INSULATION: 'Kellerdämmung',
    WINDOW_REPLACEMENT: 'Fenstererneuerung',
    SUN_PROTECTION: 'Sonnenschutz',
    HEATING_RENEWAL: 'Wärmepumpe',
    PHOTOVOLTAICS: 'PV-Anlage',
    HYDRAULIC_BALANCING: 'Heizungsoptimierung',
    NEW_PUMPS: 'Neue Pumpen',
    PIPE_INSULATION: 'Rohrdämmung',
    LED_LIGHTING: 'LED',
    VENTILATION_WITH_HEAT_RECOVERY: 'Lüftung m. WRG',
};

/**
 * i18next label keys of all measures, keyed by the stable `Measure` enum code
 * (GEN-758). The de catalog values MUST byte-match the backend German print
 * labels (= `MEASURE_CHIP_LABELS`, provenance: backend `Measure.kt`
 * chipLabels) so the UI in German and the generated PDF can never diverge —
 * no compiler enforces that cross-repo, steckbrief-variant-fields.test.ts
 * pins it.
 *
 * Some keys are deliberately SHARED with existing catalog entries whose German
 * value already matches the GEN-777 print label: 'Heat pump' (carrier +
 * heating-system option), 'Sun protection' (the #35a form field) and
 * 'Basement insulation' (the #29 form field) all carry the same German word
 * the measure now prints.
 */
export const MEASURE_LABEL_KEYS: Record<MeasureKey, string> = {
    ROOF_TOP_CEILING_INSULATION: 'Roof insulation',
    FACADE_INSULATION: 'Facade insulation',
    BASEMENT_CEILING_INSULATION: 'Basement insulation',
    WINDOW_REPLACEMENT: 'Window renewal',
    SUN_PROTECTION: 'Sun protection',
    HEATING_RENEWAL: 'Heat pump',
    PHOTOVOLTAICS: 'PV system',
    HYDRAULIC_BALANCING: 'Heating optimization',
    NEW_PUMPS: 'New pumps',
    PIPE_INSULATION: 'Pipe insulation',
    LED_LIGHTING: 'LED lighting',
    VENTILATION_WITH_HEAT_RECOVERY: 'Ventilation w. HRV',
};

/**
 * UI label of a measure code in the active language (GEN-758) — the single
 * measure-label translation point; raw code for unknown ones. Response label
 * strings (`chipLabel`, `priorityMeasures`) are never rendered directly.
 */
export function measureLabel(t: TFunction, measure: string): string {
    const key = MEASURE_LABEL_KEYS[measure as MeasureKey];
    return key === undefined ? measure : t(key);
}

/** All measure keys in backend declaration order. */
export const ALL_MEASURE_KEYS: MeasureKey[] = Object.keys(MEASURE_CHIP_LABELS) as MeasureKey[];

/**
 * Measures RETIRED from the offer (GEN-777, the GEN-753 retirement pattern) —
 * mirror of the backend's retired roster: admi no longer offers new pumps or
 * pipe insulation, so the backend proposes neither (their PROPOSAL rules are
 * gone) and the form never offers them for manual selection either. PV was
 * retired too and is offered again since the "Steckbrief recommendations v2"
 * sheet (2026-09).
 *
 * Compatibility is the hard requirement: the enum values, impact points,
 * saving fractions and (renamed) labels are KEPT on both sides — a legacy
 * stamp that selected a retired measure still renders it with its label
 * (`measureLabel`), stays deselectable in an opened Variant (one-way, like
 * the GEN-701 retiredOptions), and replays byte-identically on generation
 * (selected-but-not-proposed is a supported state; the backend stays lenient
 * on save — this form is the gate).
 */
export const RETIRED_MEASURE_KEYS: MeasureKey[] = ['NEW_PUMPS', 'PIPE_INSULATION'];

/**
 * The TEN measures admi offers (GEN-777, PV back since the "Steckbrief
 * recommendations v2" sheet): proposable by the engine and/or
 * manually addable in the measures panel — `ALL_MEASURE_KEYS` minus the
 * retired roster, in backend declaration order.
 */
export const OFFERED_MEASURE_KEYS: MeasureKey[] = ALL_MEASURE_KEYS.filter(
    (measure) => !RETIRED_MEASURE_KEYS.includes(measure),
);

const YEAR_MIN = 1500;
const YEAR_MAX = 2100;

/** Consumption years must be plausible measurement years (GEN-744). */
export const CONSUMPTION_YEAR_MIN = 1900;

/**
 * Inline error messages of the consumption pair control (GEN-744). Exported as
 * i18next keys — the control renders them through `t()`, the de catalog carries
 * the German wording.
 */
export const CONSUMPTION_PAIR_INCOMPLETE_MSG = 'Please provide both the year and the consumption value.';
export const CONSUMPTION_YEAR_RANGE_MSG = 'Year must be between 1900 and the current year.';
export const CONSUMPTION_VALUE_POSITIVE_MSG = 'The consumption value must be greater than 0.';

/**
 * Schema of a CONSUMPTION_ENTRIES value: optional overall (null = unknown), but
 * a present entry must be COMPLETE (year + value — a value without its year is
 * not honest data, GEN-744), with a plausible year (1900..current year) and a
 * positive kWh value. One array-level test instead of a nested object schema so
 * the validation error lands under the FIELD key — the form's `errors` map and
 * inline helper text machinery stay untouched.
 */
function consumptionEntriesSchema(): yup.AnySchema {
    return yup
        .array()
        .nullable()
        .test('consumption-entries', CONSUMPTION_PAIR_INCOMPLETE_MSG, function validate(entries) {
            if (entries == null) return true;
            const currentYear = new Date().getFullYear();
            for (const raw of entries as unknown[]) {
                const entry = (raw ?? {}) as Partial<ConsumptionEntry>;
                const year = entry.year ?? null;
                const kwh = entry.consumptionKwh ?? null;
                // A fully empty entry is written as null by the control — tolerated defensively.
                if (year === null && kwh === null) continue;
                if (year === null || kwh === null) {
                    return this.createError({ message: CONSUMPTION_PAIR_INCOMPLETE_MSG });
                }
                if (!Number.isInteger(year) || year < CONSUMPTION_YEAR_MIN || year > currentYear) {
                    return this.createError({ message: CONSUMPTION_YEAR_RANGE_MSG });
                }
                if (typeof kwh === 'string') {
                    return this.createError({ message: ENERGY_PRICE_NOT_A_NUMBER_MSG });
                }
                if (!(kwh > 0)) {
                    return this.createError({ message: CONSUMPTION_VALUE_POSITIVE_MSG });
                }
            }
            return true;
        });
}

/**
 * Schema of a year-capable RENOVATION tri-state (GEN-748): boolean/null like
 * every tri-state, the teilsaniert value [RENOVATION_PARTIAL_VALUE] (GEN-788,
 * exact match only), or the renovation year INLINE as the value — an integer in
 * the GEN-744 plausibility window (1900..current year, same message key). The
 * error lands under the FIELD key, so the form's inline year input shows it.
 */
function renovationYearTriStateSchema(): yup.AnySchema {
    return yup
        .mixed()
        .nullable()
        .test('renovation-year-tri-state', CONSUMPTION_YEAR_RANGE_MSG, (value) => {
            if (value === null || value === undefined || typeof value === 'boolean') return true;
            if (value === RENOVATION_PARTIAL_VALUE) return true;
            const currentYear = new Date().getFullYear();
            return (
                typeof value === 'number' &&
                Number.isInteger(value) &&
                value >= CONSUMPTION_YEAR_MIN &&
                value <= currentYear
            );
        });
}

/**
 * Schema of a tariff (GEN-788): optional on every kind (empty = the EVEBI
 * default), else 0 < price ≤ 1 €/kWh. Typed text that is no number reaches the
 * schema verbatim (see [SteckbriefVariantField.decimalInput]) and fails the
 * number type check with its own message. ANY string fails it: the decimal
 * input stores what [parseDecimalInput] can read as its number, so text left in
 * the form state is text that parser rejected — Yup's own coercion must not
 * accept it behind its back (".38", "1e-1", "0x1" would otherwise save as
 * strings the payload sends verbatim).
 */
function energyPriceSchema(): yup.NumberSchema<number | null | undefined> {
    return yup
        .number()
        .transform((value: number, original: unknown) => (typeof original === 'string' ? Number.NaN : value))
        .typeError(ENERGY_PRICE_NOT_A_NUMBER_MSG)
        .nullable()
        .moreThan(0, ENERGY_PRICE_RANGE_MSG)
        .max(ENERGY_PRICE_MAX_EUR_PER_KWH, ENERGY_PRICE_RANGE_MSG);
}

/** Schema of a manual funding percentage (GEN-779): optional, 0..100 with the inline message. */
function fundingPercentageSchema(): yup.NumberSchema<number | null | undefined> {
    return yup.number().nullable().min(0, FUNDING_PERCENTAGE_RANGE_MSG).max(100, FUNDING_PERCENTAGE_RANGE_MSG);
}

function schemaForField(field: SteckbriefVariantField): yup.AnySchema {
    switch (field.type) {
        case 'NUMBER':
            return yup.number().nullable().min(0);
        case 'YEAR':
            return yup.number().nullable().integer().min(YEAR_MIN).max(YEAR_MAX);
        case 'CONSUMPTION_ENTRIES':
            return consumptionEntriesSchema();
        case 'TRI_STATE':
            // GEN-748: flagged tri-states may carry the renovation year inline.
            return field.renovationYearInput ? renovationYearTriStateSchema() : yup.boolean().nullable();
        case 'MULTI_SELECT':
            return yup.array(yup.string()).nullable();
        case 'ATTACHMENT':
            return yup.mixed().nullable();
        case 'MONTH':
            return yup
                .string()
                .nullable()
                .matches(/^\d{4}-(0[1-9]|1[0-2])$/, AS_OF_DATE_FORMAT_MSG);
        case 'TEXT':
        case 'MULTILINE_TEXT': {
            // The input no longer clips (QA feedback round): the limit is a save-time block.
            const text = yup.string().nullable();
            return field.maxLength === undefined ? text : text.max(field.maxLength, CHARACTER_LIMIT_EXCEEDED_MSG);
        }
        case 'SELECT':
        default:
            return yup.string().nullable();
    }
}

/**
 * A consumption list holding at least one complete entry (year + kWh) — GEN-801
 * requiredness. Finite numbers only: `typeof NaN` is 'number'.
 */
function hasCompleteConsumptionEntry(value: unknown): boolean {
    if (!Array.isArray(value)) return false;
    return value.some((raw) => {
        const entry = (raw ?? {}) as Partial<ConsumptionEntry>;
        return Number.isFinite(entry.year) && Number.isFinite(entry.consumptionKwh);
    });
}

/** Whether a required value is missing: null, blank text or an empty list. */
function isBlank(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    return false;
}

/** The inline message of a missing required value, per control. */
function requiredMessage(field: SteckbriefVariantField): string {
    if (field.explicitAnswer || field.type === 'SELECT') return ANSWER_REQUIRED_MSG;
    if (field.type === 'MULTI_SELECT') return RENEWABLE_SOURCES_REQUIRED_MSG;
    if (field.type === 'CONSUMPTION_ENTRIES') return CONSUMPTION_REQUIRED_MSG;
    return REQUIRED_FIELD_MSG;
}

/**
 * Requiredness of a MANDATORY field (GEN-801) — only while the field renders
 * (a hidden conditional is nulled in the payload, so it can never be missing).
 * An [SteckbriefVariantField.explicitAnswer] field accepts "unknown" (null) once
 * the advisor chose it — the answered set comes in the validation context
 * ([ANSWERED_KEYS_CONTEXT]); "Is NGF known?" has no unknown, so it needs ja or
 * nein. A consumption needs one complete entry.
 */
function withRequired(schema: yup.AnySchema, field: SteckbriefVariantField): yup.AnySchema {
    return schema.test('required', requiredMessage(field), function required(value: unknown) {
        if (!fieldVisibleIn(field, this.parent as Record<string, unknown>)) return true;
        if (field.type === 'CONSUMPTION_ENTRIES') return hasCompleteConsumptionEntry(value);
        if (!isBlank(value)) return true;
        if (!field.explicitAnswer || field.triStateFlavor === 'BOOLEAN') return false;
        const answered = (this.options.context as Record<string, unknown> | undefined)?.[ANSWERED_KEYS_CONTEXT];
        return answered instanceof Set && answered.has(field.key);
    });
}

/**
 * A conditional field's schema applies only while the field is VISIBLE
 * ([fieldVisibleIn], chained gates included): a hidden conditional is nulled
 * in every payload, so a stale typed value (e.g. an out-of-range heating year
 * behind "No") may not block calculate or save invisibly (QA feedback round).
 */
function visibleOnly(field: SteckbriefVariantField, schema: yup.AnySchema): yup.AnySchema {
    return yup.lazy((_value: unknown, options: { parent?: unknown }) =>
        fieldVisibleIn(field, (options.parent ?? {}) as Record<string, unknown>) ? schema : yup.mixed().nullable(),
    ) as unknown as yup.AnySchema;
}

/**
 * Builds the Yup validation schema for the Variant `values` FROM the field
 * model above — never hand-edit a parallel schema.
 *
 * Requiredness follows the mapping's criticality column (MANDATORY = required,
 * GEN-801 sheet) and applies to the FINAL kind only — the kind the form starts
 * with since GEN-801; the SALES kind relaxes every field to optional (RFC-004's
 * loose-data sales semantics — the backend enforces no per-field requiredness
 * on save either, only that keys belong to the field mapping). Validate with
 * the answered set as context (see [withRequired]).
 */
export function buildVariantValuesSchema(kind: SteckbriefKind): yup.ObjectSchema<Record<string, unknown>> {
    const shape: Record<string, yup.AnySchema> = {};
    for (const field of STECKBRIEF_VARIANT_FIELDS) {
        let schema: yup.AnySchema;
        if (field.displayOnly) {
            // GEN-798 follow-up: never rendered, so never user-entered — an
            // error there could only block the save invisibly.
            shape[field.key] = yup.mixed().nullable();
            continue;
        } else if (field.key === 'investmentCostFactorPercent') {
            // QA feedback round: 1–500 %. The form seeds 100; a cleared field
            // stays savable — the backend then applies 100 (its default).
            schema = yup
                .number()
                .typeError(INVESTMENT_COST_FACTOR_RANGE_MSG)
                .nullable()
                .min(INVESTMENT_COST_FACTOR_MIN_PERCENT, INVESTMENT_COST_FACTOR_RANGE_MSG)
                .max(INVESTMENT_COST_FACTOR_MAX_PERCENT, INVESTMENT_COST_FACTOR_RANGE_MSG);
        } else if (field.key === 'windowAreaSharePercent') {
            // GEN-774 plausibility: 0 < share ≤ 100, with the inline message on
            // both bounds. Null passes this rule on every kind; FINAL requires
            // the share (GEN-801, MANDATORY) through withRequired below, SALES
            // leaves it to the backend, which then applies the configured
            // default of the Gebäudetyp (GEN-802, reported as an assumption).
            schema = yup
                .number()
                .nullable()
                .moreThan(0, WINDOW_AREA_SHARE_RANGE_MSG)
                .max(100, WINDOW_AREA_SHARE_RANGE_MSG);
        } else if (isEnergyPriceField(field.key)) {
            schema = energyPriceSchema();
        } else if (field.key === 'fundingPercentageMin') {
            // GEN-779: a percentage of the eligible costs, 0..100 (0 is a stated
            // value — the print shows it).
            schema = fundingPercentageSchema();
        } else if (field.key === 'fundingPercentageMax') {
            schema = fundingPercentageSchema().test(
                'funding-percentage-order',
                FUNDING_PERCENTAGE_ORDER_MSG,
                function validate(max) {
                    const min = (this.parent as Record<string, unknown>)['fundingPercentageMin'];
                    return typeof max !== 'number' || typeof min !== 'number' || min <= max;
                },
            );
        } else {
            schema = schemaForField(field);
        }
        if (kind === 'FINAL' && field.criticality === 'MANDATORY') schema = withRequired(schema, field);
        shape[field.key] = field.conditionalOn ? visibleOnly(field, schema) : schema;
    }
    // GEN-782: an adjusted bound must be plausible and carries a mandatory
    // reason — on every kind (the Prognose panel gates the same rules inline;
    // this is the save-time backstop for carried or seeded values). Since
    // GEN-788 a cost bound may be negative, like the H3 range it adjusts.
    for (const metric of PROGNOSIS_METRICS) {
        for (const bound of PROGNOSIS_BOUNDS) {
            const valueKey = prognosisValueKey(metric, bound);
            const number = yup.number().nullable();
            const value = metric.allowNegative ? number : number.min(0, PROGNOSIS_VALUE_NEGATIVE_MSG);
            shape[valueKey] = metric.max === null ? value : value.max(metric.max, PROGNOSIS_VALUE_RANGE_MSG);
            shape[prognosisReasonKey(metric, bound)] = yup
                .string()
                .nullable()
                .max(PROGNOSIS_REASON_MAX_LENGTH, CHARACTER_LIMIT_EXCEEDED_MSG)
                .when(valueKey, {
                    is: (adjusted: unknown) => adjusted !== null && adjusted !== undefined,
                    then: (schema) => schema.trim().required(PROGNOSIS_REASON_REQUIRED_MSG),
                });
        }
    }
    // A changed Zielklasse (either column) carries a mandatory reason, like an adjusted bound.
    for (const [stageKey, reasonKey] of [
        [TARGET_CLASS_OVERRIDE_KEY, TARGET_CLASS_OVERRIDE_REASON_KEY],
        [PRIORITY_TARGET_CLASS_OVERRIDE_KEY, PRIORITY_TARGET_CLASS_OVERRIDE_REASON_KEY],
    ]) {
        shape[reasonKey] = yup
            .string()
            .nullable()
            .max(PROGNOSIS_REASON_MAX_LENGTH, CHARACTER_LIMIT_EXCEEDED_MSG)
            .when(stageKey, {
                is: (changed: unknown) => typeof changed === 'string' && changed !== '',
                then: (schema) => schema.trim().required(PROGNOSIS_REASON_REQUIRED_MSG),
            });
    }
    return yup.object().shape(shape) as yup.ObjectSchema<Record<string, unknown>>;
}

/** Effective selected heating system: the advisor override, else the proposal. */
export function effectiveHeatingSystem(
    proposed: RecommendedHeatingSystemKey | null,
    override: RecommendedHeatingSystemKey | null,
): RecommendedHeatingSystemKey | null {
    return override ?? proposed;
}

/**
 * Seeds the heating-year gate (QA feedback round) when the prefill / stamp
 * carries no answer (a legacy or first Variant): ja when a heating year is
 * present, else unanswered — the advisor must choose. An answered gate
 * (carried or stamped) is never touched. Returns a new values map. The NGF
 * gate seeds itself in the form's initialValues (GEN-801).
 */
export function seedHeatingYearGate(values: Record<string, unknown>): Record<string, unknown> {
    if (values['heatingConstructionYearKnown'] != null) return values;
    const year = values['heatingConstructionYear'];
    return {
        ...values,
        heatingConstructionYearKnown: year !== null && year !== undefined && year !== '' ? true : null,
    };
}

/**
 * Measures that cannot be recommended for the current facts (QA feedback
 * round): the heat pump (`HEATING_RENEWAL`) with a Fernwärme carrier — the
 * engine never proposes it, and the form neither offers it as recommended nor
 * as Prio (the backend drops it defensively too).
 */
export function unavailableMeasures(values: Record<string, unknown>): MeasureKey[] {
    return values['heatingEnergyCarrier'] === DISTRICT_HEATING_CARRIER ? ['HEATING_RENEWAL'] : [];
}

/** i18next key of the hint why a measure is unavailable ([unavailableMeasures]). */
export const HEAT_PUMP_DISTRICT_HEATING_HINT_KEY = 'Not available with district heating.';
